import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback, useMemo } from "react";
import { Table, Button, Space, Tooltip, Tag, Empty, Card, Typography, Divider, message, Spin, Alert, Row, Col, Popover, Modal, Checkbox, Radio, Badge, Select, Drawer, DatePicker, Form, InputNumber, Popconfirm } from "antd";
import { DiffOutlined, MinusCircleOutlined, PlusCircleOutlined, EditOutlined, FullscreenOutlined, FullscreenExitOutlined, SwapOutlined, LinkOutlined, DisconnectOutlined, InfoCircleOutlined, QuestionCircleOutlined, SyncOutlined, SettingOutlined, DeleteOutlined, SearchOutlined, CheckOutlined } from "@ant-design/icons";
import dayjs from "dayjs"; // Added import
import { useScheduleEditor } from "../context/ScheduleEditorContext";
import FlightTimeDisplay from "../../../../components/DateTimeDisplay";
import AircraftTag from "../../../../components/AircraftTag";
import { flightOperationFlightService } from "../../../../api/enhancedApi";
import TimeCalculationService from "../services/TimeCalculationService"; // 导入 TimeCalculationService
import InlineHelpButton from "../../../../components/common/HelpButton";
import { UTCDateRangePicker } from "../../../../components/DateTimeInput";
import { pickEditorDepartureTime, pickEditorArrivalTime } from "../../../../utils/timeUtils";
import RotationVisualizer from "../../../flightOperation/Monitor/components/RotationVisualizer";

// 从 FlightMatchingService 导入常量和工具函数
import {
    DIFF_TYPE,
    MATCH_TYPE,
    MANUAL_MATCH_TYPE,
    MATCHABLE_STATUSES,
    EXCLUDED_STATUSES,
    EXECUTED_STATUSES,
    createFlightKey,
    formatMinutes,
    calculateTimeDifference,
    formatTimeValue,
    formatDateValue,
    extractRotationPath,
    generateMatchingResult,
    getFlightChangesDefault,
} from "../services/FlightMatchingService";

const { Text, Title } = Typography;

/**
 * 这个函数用于安全地访问 ScheduleEditor 上下文。
 * 如果上下文不可用，它将返回一个默认值。
 * 注意：必须在函数组件的主体中调用这个自定义 Hook。
 */
const useSafeScheduleEditor = () => {
    try {
        // 总是尝试获取上下文
        // 如果成功获取，则解构并返回，确保 update 函数存在
        const context = useScheduleEditor();
        return {
            state: context?.state || {},
            updateParameters: context?.updateParameters || (() => {}),
            updateCompareResult: context?.updateCompareResult || (() => {}),
            setDirty: context?.setDirty || (() => {}),
            removeFlight: context?.removeFlight || (() => {}), // 添加 removeFlight
            contextAvailable: true, // 添加标志位表示上下文可用
        };
    } catch (error) {
        // 如果上下文不可用，返回一个明确表示不可用的结构
        // 静默进入独立模式
        // 返回一个包含 null 更新函数和标志位的对象
        return {
            state: {},
            updateParameters: null,
            updateCompareResult: null, // 返回 null 而不是虚拟函数
            setDirty: null,
            removeFlight: null, // 上下文不可用时为 null
            contextAvailable: false, // 添加标志位表示上下文不可用
        };
    }
};

/**
 * 航班计划对比组件 - 用于对比新旧航班计划
 *
 * 作为独立Tab页使用时，该组件会自动调整其布局以充分利用可用空间
 *
 * @param {Object} props 组件属性
 * @param {Object} props.airportsMap 机场映射对象
 * @param {Object} props.scheduleData 可选的计划数据，包含schedule和flights，用于在非上下文环境中使用
 * @param {Function} props.onMatchingsChange 可选的匹配关系变更回调函数
 * @param {Function} props.onCompareResult 可选的对比结果回调函数
 * @param {string} props.operationStatusFilter 可选的执行航班状态过滤器，默认为"SCHEDULED,ACTIVE,DELAYED,DIVERTED"
 * @param {Object} ref 引用对象，用于暴露方法给父组件
 */
const FlightCompare = forwardRef(({ airportsMap, scheduleData, onMatchingsChange, onCompareResult, operationStatusFilter }, ref) => {
    // 使用我们的安全包装器获取上下文
    const editorContext = useSafeScheduleEditor();

    // 检查上下文是否实际可用 - 不是条件调用，只是检查结果
    // const contextAvailable = !!editorContext && !!editorContext.state; // 旧的检查方式

    // 使用新的 contextAvailable 标志和解构 null 函数
    const { state, updateParameters, updateCompareResult, setDirty, removeFlight, contextAvailable } = editorContext;
    const { schedule: contextSchedule, flights: contextFlights } = state || {};

    // 使用传入的scheduleData或上下文中的数据
    const schedule = scheduleData?.schedule || contextSchedule;
    const draftFlights = scheduleData?.flights || contextFlights;

    // ========== 状态管理 ==========
    // 界面状态
    const [compareVisible, setCompareVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [fullscreen, setFullscreen] = useState(false);
    const [hasSavedResult, setHasSavedResult] = useState(false);
    const [dataSource, setDataSource] = useState("api");

    // 数据状态
    const [currentOperation, setCurrentOperation] = useState(null);
    const [diffResult, setDiffResult] = useState(null);
    const [summaryExpanded, setSummaryExpanded] = useState(false); // 添加概要面板展开状态

    // 匹配相关状态
    const [matchingMode, setMatchingMode] = useState(false);
    const [selectedLeftRows, setSelectedLeftRows] = useState([]);
    const [selectedRightRows, setSelectedRightRows] = useState([]);
    const [manualMatchings, setManualMatchings] = useState([]);
    const [matchingModalVisible, setMatchingModalVisible] = useState(false);
    const [currentMatchingType, setCurrentMatchingType] = useState(MANUAL_MATCH_TYPE.MANUAL);

    // 匹配设置相关状态
    const [settingsModalVisible, setSettingsModalVisible] = useState(false);
    const [matchingSettings, setMatchingSettings] = useState({
        exactMatchThreshold: 24, // 精确匹配阈值（小时）
        fuzzyMatchThreshold: 120, // 模糊匹配阈值（小时）
        matchingStrategy: "sequential", // 匹配策略：'sequential'(顺序匹配) 或 'optimal'(最优匹配)
    });

    // 执行航班手动调整相关状态
    const [operationAdjustments, setOperationAdjustments] = useState({
        addedIds: [], // 用户手动添加的执行航班ID
        removedIds: [], // 用户手动移除的执行航班ID
    });
    const [addFlightModalVisible, setAddFlightModalVisible] = useState(false);
    const [addFlightSearchResults, setAddFlightSearchResults] = useState([]);
    const [addFlightLoading, setAddFlightLoading] = useState(false);
    // 使用 date_range 数组存储日期范围，与 UTCDateRangePicker 配合
    const [addFlightSearchParams, setAddFlightSearchParams] = useState({
        date_range: null, // dayjs 对象数组 [start, end]
    });

    // ========== 暴露方法给父组件 ==========
    useImperativeHandle(ref, () => ({
        renderChangesContent,
        loadCurrentOperations, // 暴露加载方法
    }));

    // ========== 核心业务函数 ==========
    // 获取航班变更详情
    //
    // 时间字段比较口径（与 spec "航班计划编辑器以 ETD 为准" 对齐）：
    // - draftFlight（编辑中草案）：草案没有 etd/eta 概念，仍取 std/sta
    // - operationFlight（执行航班）：按编辑器口径取值——std 字段实际比较 etd ?? std，sta 字段实际比较 eta ?? sta
    // - 起飞和到达独立判断（opField/opFallback 各自指定），互不绑定
    // 这样监控里改过 ETD 或 ETA 的航班，对比时能正确识别变更，不再出现"看着不一样、判定相同"
    const getFlightChanges = useCallback((draftFlight, operationFlight) => {
        const changes = [];
        const fieldsToCompare = [
            { key: "flight_number", label: "航班号" },
            { key: "departure_airport", label: "出发机场" },
            { key: "arrival_airport", label: "到达机场" },
            { key: "std", label: "计划起飞时间", opField: "etd", opFallback: "std" },
            { key: "sta", label: "计划到达时间", opField: "eta", opFallback: "sta" },
            { key: "stc_type", label: "航班性质" },
            { key: "flight_date", label: "航班日期" },
        ];

        fieldsToCompare.forEach(({ key, label, opField, opFallback }) => {
            // 日期字段
            if (key === "flight_date") {
                // 提取日期部分，忽略时间部分
                const getDateOnly = (dateStr) => {
                    if (!dateStr) return "";
                    try {
                        // 尝试提取日期部分 (YYYY-MM-DD)
                        return dateStr.split("T")[0] || dateStr.split(" ")[0] || dateStr;
                    } catch (e) {
                        console.error("提取日期时出错:", e);
                        return dateStr;
                    }
                };

                const oldDateOnly = getDateOnly(operationFlight[key]);
                const newDateOnly = getDateOnly(draftFlight[key]);

                if (oldDateOnly !== newDateOnly) {
                    changes.push({
                        field: key,
                        fieldLabel: label,
                        oldValue: operationFlight[key],
                        newValue: draftFlight[key],
                        // 添加日期部分，用于UI显示
                        oldDateOnly,
                        newDateOnly,
                        isDateField: true,
                    });
                }
            }
            // 时间字段
            else if (key.includes("time") || key.includes("std") || key.includes("sta")) {
                // 监控侧按编辑器口径取（ETD/ETA 优先，回退 STD/STA）；草案侧仍取 std/sta
                const oldRaw = opField ? operationFlight[opField] ?? operationFlight[opFallback] : operationFlight[key];
                const newRaw = draftFlight[key];

                const oldFormatted = formatTimeValue(oldRaw);
                const newFormatted = formatTimeValue(newRaw);

                if (oldFormatted !== newFormatted) {
                    changes.push({
                        field: key,
                        fieldLabel: label,
                        oldValue: oldRaw,
                        newValue: newRaw,
                    });
                }
            }
            // 其他字段
            else if (draftFlight[key] !== operationFlight[key]) {
                changes.push({
                    field: key,
                    fieldLabel: label,
                    oldValue: operationFlight[key],
                    newValue: draftFlight[key],
                });
            }
        });

        return changes;
    }, []);

    const generateDiffResult = useCallback(
        (draftFlights, operationFlights, savedMatchings = null) => {
            if (!Array.isArray(draftFlights) || !Array.isArray(operationFlights)) {
                console.error("生成对比结果失败: 无效的数据格式");
                return;
            }

            if (draftFlights.length === 0 && operationFlights.length === 0) {
                setDiffResult({
                    totalChanges: 0,
                    added: [],
                    removed: [],
                    changed: [],
                    unchanged: [],
                    flights: [],
                    leftData: [],
                    rightData: [],
                    rotationAnalysis: null,
                });
                return;
            }

            // 使用 FlightMatchingService 的新匹配算法
            const effectiveMatchings = savedMatchings || manualMatchings;
            const matchResult = generateMatchingResult(draftFlights, operationFlights, {
                manualMatchings: effectiveMatchings,
                matchingSettings,
                getFlightChanges,
            });

            // 服务返回的 leftData 和 rightData 已经是对齐的（类似代码diff）
            // 直接使用，无需额外转换
            const result = {
                totalChanges: matchResult.totalChanges,
                added: matchResult.added,
                removed: matchResult.removed,
                changed: matchResult.changed,
                unchanged: matchResult.unchanged,
                flights: [], // 兼容旧格式
                leftData: matchResult.leftData,
                rightData: matchResult.rightData,
                rotationAnalysis: null,
                unmatchableFlights: matchResult.unmatchableFlights,
                // 保存匹配映射，供其他组件使用
                matchMap: matchResult.matchMap,
                matchDetails: matchResult.matchDetails,
            };

            // 生成 flights 数组以兼容旧的渲染逻辑
            matchResult.leftData.forEach((leftItem, idx) => {
                result.flights.push({
                    rowKey: idx,
                    operation: leftItem.flight,
                    draft: leftItem.matchedFlight,
                    diffType: leftItem.diffType,
                    changes: leftItem.changes,
                    matchType: leftItem.matchType,
                    allDrafts: leftItem.matchedFlight ? [leftItem.matchedFlight] : [],
                    allOperations: leftItem.flight ? [leftItem.flight] : [],
                    groupId: idx,
                    isDisplayOnly: leftItem.isDisplayOnly || false,
                    isEmpty: leftItem.isEmpty || false,
                });
            });

            // 生成轮换分析
            const aircraftReg = schedule?.aircraft?.registration || "";
            if (aircraftReg) {
                try {
                    const opRotation = extractRotationPath(operationFlights);
                    const draftRotation = extractRotationPath(draftFlights);
                    result.rotationAnalysis = {
                        originalRotation: opRotation,
                        newRotation: draftRotation,
                    };
                } catch (err) {
                    console.error("生成轮换分析时出错:", err);
                    result.rotationAnalysis = null;
                }
            }

            setDiffResult(result);
        },
        [schedule, getFlightChanges, manualMatchings, matchingSettings],
    );


    // 加载当前执行计划
    const loadCurrentOperations = useCallback(
        async (savedMatchings = null, savedAdjustments = null) => {
            if (!schedule || !schedule.aircraft?.registration) {
                message.warning("请先选择飞机才能查询执行计划");
                return;
            }

            try {
                setLoading(true);
                setError(null);
                setHasSavedResult(false);
                setDataSource("api");

                // 使用保存的调整或当前状态
                const effectiveAdjustments = savedAdjustments || operationAdjustments;

                const params = {
                    aircraft_reg: schedule.aircraft.registration,
                    page_size: 500,
                    date_basis: "std",
                    // 不再做状态筛选，获取所有状态的航班供用户查看完整序列
                    // status 参数不传或传空，让后端返回所有状态
                };

                // 添加日期范围
                if (schedule.start_date) {
                    params.start_date = schedule.start_date;
                }
                if (schedule.end_date) {
                    params.end_date = schedule.end_date;
                }

                const response = await flightOperationFlightService.getFlightOperations(params);

                // 根据API_GUIDELINES.md规范，后端必须返回PaginatedResponse格式
                if (!response || !response.data || !response.data.items || typeof response.data.total !== "number") {
                    throw new Error("后端API响应不符合PaginatedResponse规范，请检查API实现");
                }

                let allFlights = response.data.items;

                // 第一步：过滤掉不应显示的航班
                // 1. CANCELLED 状态的航班
                // 2. 已被替换的航班（replaced_by_flight_id 不为空）
                allFlights = allFlights.filter((f) => {
                    // 排除 CANCELLED 状态
                    if (EXCLUDED_STATUSES.includes(f.status)) {
                        return false;
                    }
                    // 排除已被替换的航班（有 replaced_by_flight_id 字段表示已被新航班替换）
                    if (f.replaced_by_flight_id || f.replaced_id) {
                        return false;
                    }
                    return true;
                });

                // 处理手动添加的航班：如果有额外添加的ID，需要单独查询这些航班
                if (effectiveAdjustments.addedIds && effectiveAdjustments.addedIds.length > 0) {
                    // 过滤出基础查询中不存在的ID
                    const existingIds = new Set(allFlights.map((f) => f.id));
                    const idsToFetch = effectiveAdjustments.addedIds.filter((id) => !existingIds.has(id));

                    if (idsToFetch.length > 0) {
                        try {
                            // 逐个查询手动添加的航班（或可以实现批量查询接口）
                            const additionalFlights = [];
                            for (const flightId of idsToFetch) {
                                try {
                                    const flightResponse = await flightOperationFlightService.getFlightOperationById(flightId);
                                    if (flightResponse?.data) {
                                        additionalFlights.push(flightResponse.data);
                                    }
                                } catch (fetchErr) {
                                    console.warn(`无法加载手动添加的航班 ID ${flightId}:`, fetchErr);
                                }
                            }
                            allFlights = [...allFlights, ...additionalFlights];
                        } catch (addErr) {
                            console.error("加载手动添加的航班失败:", addErr);
                        }
                    }
                }

                // 处理手动删除的航班：排除用户标记为删除的航班
                if (effectiveAdjustments.removedIds && effectiveAdjustments.removedIds.length > 0) {
                    const removedIdSet = new Set(effectiveAdjustments.removedIds);
                    allFlights = allFlights.filter((f) => !removedIdSet.has(f.id));
                }

                // 为每个航班添加 isMatchable 标记，用于区分可参与匹配的航班
                const processedFlights = allFlights.map((flight) => ({
                    ...flight,
                    isMatchable: MATCHABLE_STATUSES.includes(flight.status),
                }));

                // 构造格式化的operationData对象
                const operationData = {
                    name: "当前执行计划",
                    flights: processedFlights,
                };

                setCurrentOperation(operationData);

                // 恢复保存的调整
                if (savedAdjustments) {
                    setOperationAdjustments(savedAdjustments);
                }

                if (savedMatchings && Array.isArray(savedMatchings)) {
                    setManualMatchings(savedMatchings);
                    generateDiffResult(draftFlights, operationData.flights, savedMatchings);
                } else {
                    generateDiffResult(draftFlights, operationData.flights);
                }

                setCompareVisible(true);
            } catch (err) {
                console.error("加载航班执行记录失败:", err);
                setError(`加载航班执行记录失败: ${err.message || "未知错误"}`);
                message.error(`加载航班执行记录失败: ${err.message || "未知错误"}`);
            } finally {
                setLoading(false);
            }
        },
        [schedule, draftFlights, generateDiffResult, operationAdjustments],
    );

    // ========== 手动调整执行航班的函数 ==========
    // 执行删除航班的实际操作（需要在 removeOperationFlight 之前定义）
    const performRemoveFlight = useCallback(
        (flightId) => {
            setOperationAdjustments((prev) => {
                const newAdjustments = {
                    ...prev,
                    removedIds: [...prev.removedIds, flightId],
                    // 如果这个航班之前是手动添加的，从添加列表中移除
                    addedIds: prev.addedIds.filter((id) => id !== flightId),
                };
                return newAdjustments;
            });

            // 更新当前操作数据并重新生成对比结果
            if (currentOperation?.flights) {
                const updatedFlights = currentOperation.flights.filter((f) => f.id !== flightId);
                const updatedOperation = { ...currentOperation, flights: updatedFlights };
                setCurrentOperation(updatedOperation);
                generateDiffResult(draftFlights, updatedFlights, manualMatchings);
            }

            setDirty && setDirty();
            message.success("已从对比列表中移除航班");
        },
        [currentOperation, draftFlights, generateDiffResult, manualMatchings, setDirty],
    );

    // 手动删除执行航班（从对比列表中移除）
    const removeOperationFlight = useCallback(
        (flightId) => {
            if (!flightId) return;

            // 检查是否有关联的手动匹配
            const hasManualMatch = manualMatchings.some((match) => match.leftIds && match.leftIds.some((id) => String(id) === String(flightId)));

            if (hasManualMatch) {
                Modal.confirm({
                    title: "该航班存在手动匹配关系",
                    content: "移除此航班将同时解除相关的手动匹配关系。是否继续？",
                    onOk: () => {
                        // 先清除关联的手动匹配
                        const newMatchings = manualMatchings.filter((match) => !(match.leftIds && match.leftIds.some((id) => String(id) === String(flightId))));
                        setManualMatchings(newMatchings);

                        // 然后添加到删除列表
                        performRemoveFlight(flightId);
                    },
                });
            } else {
                performRemoveFlight(flightId);
            }
        },
        [manualMatchings, performRemoveFlight],
    );

    // 搜索航班（用于添加航班 Modal）
    const searchFlightsToAdd = useCallback(
        async (searchParams) => {
            if (!schedule?.aircraft?.registration) {
                message.warning("请先选择飞机");
                return;
            }

            try {
                setAddFlightLoading(true);

                const params = {
                    aircraft_reg: schedule.aircraft.registration,
                    page_size: 100,
                    date_basis: "std",
                };

                // 从 date_range 数组提取开始和结束日期
                if (searchParams.date_range && Array.isArray(searchParams.date_range) && searchParams.date_range.length === 2) {
                    const [startDate, endDate] = searchParams.date_range;
                    if (startDate) {
                        // UTCDateRangePicker 返回的是 dayjs 对象，格式化为 YYYY-MM-DD
                        params.start_date = dayjs.isDayjs(startDate) ? startDate.format("YYYY-MM-DD") : startDate;
                    }
                    if (endDate) {
                        params.end_date = dayjs.isDayjs(endDate) ? endDate.format("YYYY-MM-DD") : endDate;
                    }
                }

                const response = await flightOperationFlightService.getFlightOperations(params);

                if (response?.data?.items) {
                    // 标记哪些航班已经在当前对比列表中
                    const currentFlightIds = new Set(currentOperation?.flights?.map((f) => f.id) || []);
                    const removedIds = new Set(operationAdjustments.removedIds || []);

                    // 过滤掉无效状态的航班（CANCELLED, PLAN_CANCELLED, REPLACED）
                    const filteredItems = response.data.items.filter((flight) => !EXCLUDED_STATUSES.includes(flight.status));

                    const processedResults = filteredItems.map((flight) => ({
                        ...flight,
                        isInCurrentList: currentFlightIds.has(flight.id),
                        isRemoved: removedIds.has(flight.id),
                        isMatchable: MATCHABLE_STATUSES.includes(flight.status),
                    }));

                    setAddFlightSearchResults(processedResults);
                }
            } catch (err) {
                console.error("搜索航班失败:", err);
                message.error("搜索航班失败");
            } finally {
                setAddFlightLoading(false);
            }
        },
        [schedule, currentOperation, operationAdjustments],
    );

    // 添加航班到对比列表
    const addOperationFlight = useCallback(
        (flight) => {
            if (!flight?.id) return;

            // 如果是之前删除的航班，从删除列表中移除
            const wasRemoved = operationAdjustments.removedIds.includes(flight.id);

            setOperationAdjustments((prev) => {
                const newAdjustments = {
                    ...prev,
                    removedIds: prev.removedIds.filter((id) => id !== flight.id),
                    // 如果不是之前删除的（即真正的新增），添加到添加列表
                    addedIds: wasRemoved ? prev.addedIds : [...prev.addedIds.filter((id) => id !== flight.id), flight.id],
                };
                return newAdjustments;
            });

            // 更新当前操作数据
            if (currentOperation) {
                // 检查是否已存在
                const exists = currentOperation.flights.some((f) => f.id === flight.id);
                if (!exists) {
                    const processedFlight = {
                        ...flight,
                        isMatchable: MATCHABLE_STATUSES.includes(flight.status),
                    };
                    const updatedFlights = [...currentOperation.flights, processedFlight].sort((a, b) => new Date(a.std) - new Date(b.std));
                    const updatedOperation = { ...currentOperation, flights: updatedFlights };
                    setCurrentOperation(updatedOperation);
                    generateDiffResult(draftFlights, updatedFlights, manualMatchings);
                }
            }

            setDirty && setDirty();
            message.success("已添加航班到对比列表");

            // 更新搜索结果中的状态
            setAddFlightSearchResults((prev) => prev.map((f) => (f.id === flight.id ? { ...f, isInCurrentList: true, isRemoved: false } : f)));
        },
        [currentOperation, draftFlights, generateDiffResult, manualMatchings, operationAdjustments, setDirty],
    );

    // 重置所有手动调整
    const resetOperationAdjustments = useCallback(() => {
        Modal.confirm({
            title: "确定重置所有手动调整吗？",
            content: "这将恢复原始的执行航班列表，删除所有手动添加和移除的操作。",
            onOk: () => {
                setOperationAdjustments({ addedIds: [], removedIds: [] });
                setDirty && setDirty();
                // 重新加载数据
                loadCurrentOperations(manualMatchings, { addedIds: [], removedIds: [] });
                message.success("已重置所有手动调整");
            },
        });
    }, [loadCurrentOperations, manualMatchings, setDirty]);

    // ========== UI辅助函数 ==========
    // 获取差异标签
    const getDiffTag = useCallback((diffType) => {
        switch (diffType) {
            case DIFF_TYPE.ADDED:
                return (
                    <Tag color="success" icon={<PlusCircleOutlined />} style={{ display: "inline-flex", margin: "2px 0" }}>
                        新增
                    </Tag>
                );
            case DIFF_TYPE.REMOVED:
                return (
                    <Tag color="grey" icon={<MinusCircleOutlined />} style={{ display: "inline-flex", margin: "2px 0" }}>
                        取消
                    </Tag>
                );
            case DIFF_TYPE.CHANGED:
                return (
                    <Tag color="warning" icon={<EditOutlined />} style={{ display: "inline-flex", margin: "2px 0" }}>
                        变更
                    </Tag>
                );
            case "DISPLAY_ONLY":
                return (
                    <Tooltip title="此航班仅供参考显示，不参与航班对比和发布">
                        <Tag color="default" style={{ display: "inline-flex", margin: "2px 0", opacity: 0.7, cursor: "help" }}>
                            仅显示
                        </Tag>
                    </Tooltip>
                );
            case "EXECUTED_IGNORED":
                return (
                    <Tooltip title="此航班已执行，不参与差异对比。也不会参与航班发布。">
                        <Tag style={{ display: "inline-flex", margin: "2px 0", color: "#999", borderColor: "#d9d9d9" }}>忽略</Tag>
                    </Tooltip>
                );
            default:
                return <Tag style={{ display: "inline-flex", margin: "2px 0" }}>相同</Tag>;
        }
    }, []);

    // 获取状态标签 - 改为使用 Badge
    const getStatusTag = useCallback((status) => {
        if (!status) return null;

        const statusMap = {
            ACTIVE: { status: "processing", text: "执行中" },
            SCHEDULED: { status: "default", text: "已排定" },
            COMPLETED: { status: "success", text: "已完成" },
            CANCELLED: { status: "error", text: "已取消" },
            DELAYED: { status: "warning", text: "延误" },
            DIVERTED: { status: "warning", text: "备降" },
            DEPARTED: { status: "processing", text: "已起飞" },
            PLAN_CANCELLED: { status: "error", text: "计划取消" },
            REPLACED: { status: "error", text: "已替换" },
        };

        const statusInfo = statusMap[status] || { status: "default", text: status };
        return <Badge className="status-badge" status={statusInfo.status} text={statusInfo.text} />;
    }, []);

    // 获取完整的变更信息
    const getCompleteChanges = useCallback((record) => {
        if (!record || !record.changes || record.changes.length === 0) {
            // 根据diffType生成一些基本变更信息
            if (record.diffType === DIFF_TYPE.ADDED) {
                return [
                    {
                        field: "status",
                        fieldLabel: "状态",
                        oldValue: "不存在",
                        newValue: "新增航班",
                    },
                ];
            } else if (record.diffType === DIFF_TYPE.REMOVED) {
                return [
                    {
                        field: "status",
                        fieldLabel: "状态",
                        oldValue: record.flight?.status || "已排定",
                        newValue: "已删除",
                    },
                ];
            }
            return [];
        }
        return record.changes;
    }, []);

    // 统一的空行处理组件
    const EmptyCell = useCallback(
        ({ isPlaceholder = false }) => (
            <div className={`empty-cell ${isPlaceholder ? "placeholder-cell" : ""}`}>
                {isPlaceholder ? (
                    <div className="empty-cell-spacer"></div>
                ) : (
                    <>
                        <div className="empty-cell-content">
                            <span style={{ color: "#ccc" }}>--</span>
                        </div>
                        <div className="empty-cell-spacer" aria-hidden="true"></div>
                    </>
                )}
            </div>
        ),
        [],
    );

    // 获取行样式
    const getRowClassName = useCallback((record) => {
        let classNames = [];

        if (record.isEmpty) {
            classNames.push("empty-row");
        }

        // 标记组内航班
        if (record.isGroupStart) {
            classNames.push("group-start-row");
        }

        if (record.isGroupEnd) {
            classNames.push("group-end-row");
        }

        // 标记组ID，用于高亮整组
        classNames.push(`group-${record.groupId}`);

        // 添加差异类型样式
        switch (record.diffType) {
            case DIFF_TYPE.ADDED:
                classNames.push("diff-row-added");
                break;
            case DIFF_TYPE.REMOVED:
                classNames.push("diff-row-removed");
                break;
            case DIFF_TYPE.CHANGED:
                classNames.push("diff-row-changed");
                break;
            default:
                // 不添加额外样式
                break;
        }

        return classNames.join(" ");
    }, []);

    // 渲染对比统计信息
    const renderStats = useCallback(() => {
        if (!diffResult) return null;

        return (
            <Space size="large">
                <Tooltip title="草案中新增的航班">
                    <Text>
                        新增: <Text type="success">{diffResult.added.length}</Text>
                    </Text>
                </Tooltip>
                <Tooltip title="草案中取消的航班">
                    <Text>
                        航班取消: <Text type="danger">{diffResult.removed.length}</Text>
                    </Text>
                </Tooltip>
                <Tooltip title="草案中变更的航班">
                    <Text>
                        变更: <Text type="warning">{diffResult.changed.length}</Text>
                    </Text>
                </Tooltip>
                <Tooltip title="未变更的航班">
                    <Text>
                        未变更: <Text>{diffResult.unchanged.length}</Text>
                    </Text>
                </Tooltip>
                <Tooltip title="变更总数">
                    <Text strong>
                        变更总数: <Text strong>{diffResult.totalChanges}</Text>
                    </Text>
                </Tooltip>
            </Space>
        );
    }, [diffResult]);

    // 渲染变更详情提示内容
    const renderChangesContent = useCallback((changes) => {
        if (!changes || changes.length === 0) return null;

        return (
            <div style={{ fontSize: "12px", maxWidth: "300px" }}>
                <div style={{ fontWeight: "bold", marginBottom: 8, borderBottom: "1px solid #f0f0f0", paddingBottom: 4 }}>变更详情</div>
                {changes.map((change, idx) => {
                    // 判断是否是时间相关字段
                    const isTimeField = change.field.includes("time") || change.field.includes("std") || change.field.includes("sta");
                    const isDateField = change.field.includes("date");

                    // 计算时间差异（仅对时间字段）
                    const timeDifference = isTimeField ? calculateTimeDifference(change.oldValue, change.newValue) : null;

                    // 格式化显示值
                    const oldDisplayValue = isDateField ? formatDateValue(change.oldValue) : isTimeField ? formatTimeValue(change.oldValue) : change.oldValue;

                    const newDisplayValue = isDateField ? formatDateValue(change.newValue) : isTimeField ? formatTimeValue(change.newValue) : change.newValue;

                    return (
                        <div key={idx} style={{ marginBottom: 12 }}>
                            <div style={{ fontWeight: "bold", marginBottom: 2 }}>{change.fieldLabel}:</div>
                            <div style={{ display: "flex", flexDirection: "column", marginLeft: 8 }}>
                                <div style={{ textDecoration: "line-through", color: "#ff4d4f", marginBottom: 3 }}>{oldDisplayValue}</div>
                                <div style={{ color: "#52c41a", display: "flex", alignItems: "center" }}>
                                    {newDisplayValue}
                                    {timeDifference && (
                                        <span
                                            style={{
                                                marginLeft: 8,
                                                fontWeight: "bold",
                                                fontSize: "11px",
                                                padding: "1px 4px",
                                                borderRadius: "2px",
                                                backgroundColor: timeDifference.startsWith("+") ? "rgba(82, 196, 26, 0.1)" : "rgba(255, 77, 79, 0.1)",
                                                color: timeDifference.startsWith("+") ? "#52c41a" : "#ff4d4f",
                                            }}
                                        >
                                            {timeDifference}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }, []);

    // ========== 交互处理函数 ==========
    // 切换全屏模式
    const toggleFullscreen = useCallback(() => {
        setFullscreen(!fullscreen);
    }, [fullscreen]);

    // 切换匹配模式
    const toggleMatchingMode = useCallback(() => {
        setMatchingMode(!matchingMode);
        // 退出匹配模式时清空选中状态
        if (matchingMode) {
            setSelectedLeftRows([]);
            setSelectedRightRows([]);
        }
    }, [matchingMode]);

    // 选择行处理
    const handleRowSelection = useCallback(
        (record, tableType) => {
            if (!matchingMode) return; // 非匹配模式下不处理

            if (tableType === "left") {
                // 检查是否已选中
                const isSelected = selectedLeftRows.some((item) => item.rowKey === record.rowKey && item.subRowKey === record.subRowKey);

                if (isSelected) {
                    // 取消选中
                    setSelectedLeftRows((prev) => prev.filter((item) => !(item.rowKey === record.rowKey && item.subRowKey === record.subRowKey)));
                } else {
                    // 选中行
                    setSelectedLeftRows((prev) => [...prev, record]);
                }
            } else {
                // 右侧表格选择
                const isSelected = selectedRightRows.some((item) => item.rowKey === record.rowKey && item.subRowKey === record.subRowKey);

                if (isSelected) {
                    // 取消选中
                    setSelectedRightRows((prev) => prev.filter((item) => !(item.rowKey === record.rowKey && item.subRowKey === record.subRowKey)));
                } else {
                    // 选中行
                    setSelectedRightRows((prev) => [...prev, record]);
                }
            }
        },
        [matchingMode, selectedLeftRows, selectedRightRows],
    );

    // 创建匹配
    const createMatching = useCallback(() => {
        // 检查是否有选中的行
        if (selectedLeftRows.length === 0 && selectedRightRows.length === 0) {
            message.warning("请至少选择一行进行匹配");
            return;
        }

        // 打开匹配对话框
        setMatchingModalVisible(true);
    }, [selectedLeftRows, selectedRightRows]);

    // 确认创建匹配
    const confirmCreateMatching = useCallback(() => {
        console.log("🔧 创建手动匹配调试:");
        console.log(
            "选中的左侧行:",
            selectedLeftRows.map((row) => ({
                rowKey: row.rowKey,
                subRowKey: row.subRowKey,
                flight: row.flight
                    ? {
                          id: row.flight.id,
                          flight_number: row.flight.flight_number,
                          std: row.flight.std,
                          readable_time: new Date(row.flight.std).toLocaleString(),
                      }
                    : null,
            })),
        );
        console.log(
            "选中的右侧行:",
            selectedRightRows.map((row) => ({
                rowKey: row.rowKey,
                subRowKey: row.subRowKey,
                flight: row.flight
                    ? {
                          id: row.flight.id,
                          flight_number: row.flight.flight_number,
                          std: row.flight.std,
                          readable_time: new Date(row.flight.std).toLocaleString(),
                      }
                    : null,
            })),
        );

        // 获取左侧选中行的ID
        const leftFlightIds = selectedLeftRows
            .map((row) => {
                const flight = row.flight;
                return flight?.id;
            })
            .filter(Boolean);

        // 获取右侧航班的自然键
        const rightFlightKeys = selectedRightRows
            .map((row) => {
                const flight = row.flight;
                // 创建稳定的自然键
                if (!flight) return null;
                return `${flight.flight_number}_${flight.departure_airport}_${flight.arrival_airport}_${flight.std}`;
            })
            .filter(Boolean);

        console.log("提取的左侧航班ID:", leftFlightIds);
        console.log("提取的右侧航班键:", rightFlightKeys);

        // 创建新的匹配关系
        const newMatching = {
            id: `manual_${Date.now()}`, // 生成唯一ID
            leftIds: leftFlightIds, // 使用ID存储左侧航班
            rightKeys: rightFlightKeys, // 使用自然键存储右侧航班
            type: currentMatchingType,
            isCustom: true,
            createdAt: new Date().toISOString(),
        };

        // 更新匹配关系
        setManualMatchings((prev) => [...prev, newMatching]);
        setDirty && setDirty(); // 标记状态已变更（如果上下文可用）

        // 清空选中状态
        setSelectedLeftRows([]);
        setSelectedRightRows([]);

        // 关闭对话框
        setMatchingModalVisible(false);

        // 重新生成对比结果
        if (currentOperation?.flights) {
            generateDiffResult(draftFlights, currentOperation.flights);
        }

        message.success("成功创建匹配关系");
    }, [selectedLeftRows, selectedRightRows, currentMatchingType, currentOperation, draftFlights, generateDiffResult, setDirty]);

    // 删除匹配关系
    const deleteMatching = useCallback(
        (matchId) => {
            // 从匹配列表中移除
            setManualMatchings((prev) => prev.filter((match) => match.id !== matchId));
            setDirty && setDirty(); // 标记状态已变更（如果上下文可用）

            // 重新生成对比结果
            if (currentOperation?.flights) {
                generateDiffResult(draftFlights, currentOperation.flights);
            }

            message.success("成功删除匹配关系");
        },
        [currentOperation, draftFlights, generateDiffResult, setDirty],
    );

    // 重置所有匹配
    const resetAllMatchings = useCallback(() => {
        Modal.confirm({
            title: "确定重置所有手动匹配吗?",
            content: "这将删除所有已创建的手动匹配关系，并重新使用自动匹配。",
            onOk: () => {
                setManualMatchings([]);
                setDirty && setDirty(); // 标记状态已变更（如果上下文可用）

                // 重新生成对比结果
                if (currentOperation?.flights) {
                    generateDiffResult(draftFlights, currentOperation.flights);
                }

                message.success("已重置所有手动匹配");
            },
        });
    }, [currentOperation, draftFlights, generateDiffResult, setDirty]);

    // 处理删除草稿航班
    const handleRemoveDraftFlight = useCallback(
        (flightId) => {
            if (!flightId || !removeFlight) return;
            removeFlight(flightId);
            message.success("已删除航班");
        },
        [removeFlight],
    );

    // ========== 表格列定义 ==========
    // 左侧表格列 - 执行计划
    const leftColumns = useMemo(
        () => [
            // 序号
            {
                title: "序号",
                dataIndex: "rowKey",
                key: "rowKey",
                width: "6%",
                align: "center",
                render: (_, record) => {
                    if (record.isGroupStart) {
                        return record.rowKey + 1;
                    }
                    return "";
                },
            },
            {
                title: "DATE",
                dataIndex: "flight_date",
                key: "flight_date",
                width: "8%",
                render: (_, record) => {
                    if (record.isEmpty) return <EmptyCell />;
                    if (record.flight?.flight_date) {
                        const date = record.flight.flight_date;
                        return (
                            <Tooltip title={dayjs.utc(date).format("YYYY-MM-DD")}>
                                <span>
                                    {dayjs
                                        .utc(date)
                                        .format("D-MMM")
                                        .replace(/[^\d\-A-Za-z]/g, "")}
                                </span>
                            </Tooltip>
                        );
                    }
                    return "";
                },
            },
            {
                title: (
                    <span style={{ whiteSpace: "nowrap" }}>
                        <Tooltip title="Day of Week">D</Tooltip>
                    </span>
                ),
                dataIndex: "day_of_week",
                key: "day_of_week",
                width: "4%",
                align: "center",
                render: (_, record) => {
                    if (record.isEmpty) return <EmptyCell />;
                    const dateToUse = record.flight?.std || record.flight?.flight_date;
                    if (dateToUse) {
                        // 使用 TimeCalculationService.getDayOfWeek 返回数字表示 (1-7)
                        return TimeCalculationService.getDayOfWeek(dateToUse);
                    }
                    return "";
                },
            },
            {
                title: "FLT",
                dataIndex: "flight_number",
                key: "flight_number",
                width: "10%",
                render: (_, record) => {
                    if (record.isEmpty) return <EmptyCell />;
                    return record.flight?.flight_number || "";
                },
            },
            {
                title: (
                    <Tooltip title="Rotation">
                        <span style={{ whiteSpace: "nowrap" }}>ROT</span>
                    </Tooltip>
                ),
                dataIndex: "rotation_no",
                key: "rotation_no",
                width: "6%",
                align: "center",
                render: (_, record) => {
                    if (record.isEmpty) return <EmptyCell />;
                    if (!record.flight) return <EmptyCell />;
                    return <RotationVisualizer flight={record.flight} allFlights={currentOperation?.flights || []} />;
                },
            },
            {
                title: (
                    <span style={{ whiteSpace: "nowrap" }}>
                        DEP/ARR{" "}
                        <Popover
                            content={
                                <div style={{ maxWidth: 420, fontSize: 13, lineHeight: 1.8 }}>
                                    <div style={{ fontWeight: "bold", marginBottom: 8, fontSize: 14 }}>订单号规则</div>
                                    <p style={{ margin: "0 0 8px" }}>
                                        首次发布时，系统按"轮换组"（Rotation）自动生成订单号。每个轮换组分配一个序号，代表本月第几个轮换组。去程（拉货）航班使用 CB 前缀（如 CB-2603-001），回程（定位）航班使用 CR 前缀（如 CR-2603-001），两者共享同一序号。
                                    </p>
                                    <p style={{ margin: "0 0 8px" }}>
                                        计划变更时，系统将新计划与当前执行中的航班匹配。匹配成功的航班，订单号自动从旧航班延续到新航班——无论时间、航路如何调整，只要是同一趟飞行任务，订单号不变。
                                    </p>
                                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                                        <li>航班替换：新航班直接继承旧航班的订单号</li>
                                        <li>航班拆分（如备降）：所有航段共享原航班订单号</li>
                                        <li>航班合并（如经停改直飞）：同一订单自动继承，不同订单需人工确认</li>
                                        <li>新增航班：归属已有轮换组则关联现有订单，全新轮换组则生成新订单号</li>
                                        <li>被取消/替换的航班：保留原订单号，用于成本归集</li>
                                    </ul>
                                </div>
                            }
                            trigger="hover"
                            placement="bottomLeft"
                        >
                            <QuestionCircleOutlined style={{ fontSize: 11, color: "#999", cursor: "help" }} />
                        </Popover>
                    </span>
                ),
                dataIndex: "airports",
                key: "airports",
                width: "16%",
                render: (_, record) => {
                    if (record.isEmpty) return <EmptyCell />;
                    const bookingNo = record.flight?.booking_no;
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
                            <div>{`${record.flight?.departure_airport || ""} → ${record.flight?.arrival_airport || ""}`}</div>
                            {bookingNo && (
                                <div style={{ fontSize: 11, color: '#999', lineHeight: '16px' }}>
                                    {bookingNo}
                                </div>
                            )}
                        </div>
                    );
                },
            },
            {
                title: (
                    <span style={{ whiteSpace: "nowrap" }}>
                        时间{" "}
                        <Tooltip title="显示优先级: 实际时间(ATD/ATA) → 预计时间(ETD/ETA) → 计划时间(STD/STA)">
                            <InfoCircleOutlined style={{ fontSize: "11px", color: "#999", cursor: "help" }} />
                        </Tooltip>
                    </span>
                ),
                dataIndex: "time",
                key: "time",
                align: "center",
                width: "16%",
                render: (_, record) => {
                    if (record.isEmpty) return <EmptyCell />;
                    const flight = record.flight;
                    const depAirport = flight?.departure_airport;
                    const arrAirport = flight?.arrival_airport;

                    return (
                        <div className="time-display-wrapper" style={{ display: "flex", alignItems: "center" }}>
                            <div>
                                {/* 编辑器口径：ETD 优先、回退 STD；ETA 优先、回退 STA；ATD/ATA 不参与，
                                    避免把已发生的延误带进未来计划。跟差异判断口径一致，杜绝"看着不一样、判定相同"。 */}
                                <FlightTimeDisplay utcTime={pickEditorDepartureTime(flight)} airportCode={depAirport} airportsMap={airportsMap} />
                                <FlightTimeDisplay utcTime={pickEditorArrivalTime(flight)} airportCode={arrAirport} airportsMap={airportsMap} />
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", fontSize: "12px", color: "#999", marginLeft: 12 }}>
                                <Tooltip
                                    title={
                                        <span>
                                            Block Hour: {formatMinutes(flight?.block_time_minutes)}
                                            <br />
                                            Ground Time: {formatMinutes(flight?.ground_time_minutes)}
                                        </span>
                                    }
                                >
                                    <div>{flight?.block_time_minutes ? `${formatMinutes(flight.block_time_minutes)}` : ""}</div>
                                    <div>{flight?.ground_time_minutes ? `${formatMinutes(flight.ground_time_minutes)}` : ""}</div>
                                </Tooltip>
                            </div>
                        </div>
                    );
                },
            },
            {
                title: "STC",
                dataIndex: "stc_type",
                key: "stc_type",
                width: "8%",
                render: (_, record) => {
                    if (record.isEmpty) return <EmptyCell />;
                    return record.flight?.stc_type || "";
                },
            },
            {
                title: "差异",
                dataIndex: "diffType",
                key: "diffType",
                width: "11%",
                render: (_, record) => {
                    if (record.isEmpty) return <EmptyCell />;

                    // 只在组的第一行显示差异
                    if (!record.isGroupStart) {
                        return null;
                    }

                    // 获取变更信息
                    const changes = getCompleteChanges(record);
                    const hasChanges = changes && changes.length > 0;

                    const diffTag = getDiffTag(record.diffType);

                    // 检查是否有手动匹配
                    const hasManualMatch =
                        record.flight &&
                        manualMatchings.some((match) => {
                            if (!record.flight) return false;
                            return match.leftIds && record.flight.id && match.leftIds.some((id) => String(id) === String(record.flight.id));
                        });

                    return (
                        <div>
                            {hasChanges ? (
                                <Popover content={renderChangesContent(changes)} trigger="hover" placement="rightTop">
                                    <div className="diff-tag-with-info">
                                        {diffTag}
                                        {hasManualMatch && (
                                            <Tag className="manual-matching-tag" color="blue">
                                                <LinkOutlined /> 手动匹配
                                            </Tag>
                                        )}
                                    </div>
                                </Popover>
                            ) : (
                                <div className="diff-tag-with-info">
                                    {diffTag}
                                    {hasManualMatch && (
                                        <Tag className="manual-matching-tag" color="blue">
                                            <LinkOutlined /> 手动匹配
                                        </Tag>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                },
            },
            // 状态列 - 显示航班执行状态，并包含悬浮显示的删除按钮
            {
                title: "状态",
                dataIndex: "status",
                key: "status",
                width: "10%",
                render: (_, record) => {
                    if (record.isEmpty) return <EmptyCell />;
                    const status = record.flight?.status;

                    return (
                        <div
                            style={{ position: "relative", minHeight: "24px" }}
                            onMouseEnter={(e) => {
                                const btnOverlay = e.currentTarget.querySelector(".hover-delete-overlay");
                                if (btnOverlay) btnOverlay.style.display = "flex";
                            }}
                            onMouseLeave={(e) => {
                                const btnOverlay = e.currentTarget.querySelector(".hover-delete-overlay");
                                if (btnOverlay) btnOverlay.style.display = "none";
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", height: "100%" }}>{status ? getStatusTag(status) : ""}</div>

                            {record.flight?.id && (
                                <div
                                    className="hover-delete-overlay"
                                    style={{
                                        display: "none",
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        zIndex: 1,
                                    }}
                                >
                                    <Tooltip title="从对比列表中移除此航班">
                                        <Popconfirm
                                            title="确定要移除该航班吗？"
                                            onConfirm={(e) => {
                                                e.stopPropagation();
                                                removeOperationFlight(record.flight.id);
                                            }}
                                            okText="是"
                                            cancelText="否"
                                        >
                                            <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} />
                                        </Popconfirm>
                                    </Tooltip>
                                </div>
                            )}
                        </div>
                    );
                },
            },
        ],
        [EmptyCell, getCompleteChanges, getDiffTag, getStatusTag, manualMatchings, renderChangesContent, airportsMap, removeOperationFlight, currentOperation],
    );

    // 右侧表格列 - 编辑中计划
    const rightColumns = useMemo(
        () => [
            {
                title: "差异",
                dataIndex: "diffType",
                key: "diffType",
                width: "10%",
                render: (_, record) => {
                    if (record.isEmpty) return <EmptyCell />;

                    // 只在组的第一行显示差异
                    if (!record.isGroupStart) {
                        return null;
                    }

                    const changes = getCompleteChanges(record);
                    const hasChanges = changes && changes.length > 0;

                    const diffTag = getDiffTag(record.diffType);

                    // 检查是否有手动匹配
                    const hasManualMatch =
                        record.flight &&
                        manualMatchings.some((match) => {
                            if (!record.flight) return false;
                            const flightKey = `${record.flight.flight_number}_${record.flight.departure_airport}_${record.flight.arrival_airport}_${record.flight.std}`;
                            return (match.rightKeys && match.rightKeys.includes(flightKey)) || (match.rightIds && record.flight.id && match.rightIds.some((id) => String(id) === String(record.flight.id)));
                        });

                    return (
                        <div>
                            {hasChanges ? (
                                <Popover content={renderChangesContent(changes)} trigger="hover" placement="right">
                                    <div className="diff-tag-with-info">
                                        {diffTag}
                                        {hasManualMatch && (
                                            <Tag className="manual-matching-tag" color="blue">
                                                <LinkOutlined /> 手动匹配
                                            </Tag>
                                        )}
                                    </div>
                                </Popover>
                            ) : (
                                <div className="diff-tag-with-info">
                                    {diffTag}
                                    {hasManualMatch && (
                                        <Tag className="manual-matching-tag" color="blue">
                                            <LinkOutlined /> 手动匹配
                                        </Tag>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                },
            },
            {
                title: "DATE",
                dataIndex: "flight_date",
                key: "flight_date",
                width: "6%",
                render: (_, record) => {
                    if (record.isEmpty) return <EmptyCell />;
                    if (record.flight?.flight_date) {
                        const date = record.flight.flight_date;
                        return (
                            <Tooltip title={dayjs.utc(date).format("YYYY-MM-DD")}>
                                <span>
                                    {dayjs
                                        .utc(date)
                                        .format("D-MMM")
                                        .replace(/[^\d\-A-Za-z]/g, "")}
                                </span>
                            </Tooltip>
                        );
                    }
                    return "";
                },
            },
            {
                title: (
                    <span style={{ whiteSpace: "nowrap" }}>
                        <Tooltip title="Day of Week">D</Tooltip>
                    </span>
                ),
                dataIndex: "day_of_week",
                key: "day_of_week",
                width: "3%",
                align: "center",
                render: (_, record) => {
                    if (record.isEmpty) return <EmptyCell />;
                    const dateToUse = record.flight?.std || record.flight?.flight_date;
                    if (dateToUse) {
                        return TimeCalculationService.getDayOfWeek(dateToUse);
                    }
                    return "";
                },
            },
            {
                title: "FLT",
                dataIndex: "flight_number",
                key: "flight_number",
                width: "8%",
                render: (_, record) => {
                    if (record.isEmpty) return <EmptyCell />;
                    return record.flight?.flight_number || "";
                },
            },
            {
                title: (
                    <Tooltip title="Rotation">
                        <span style={{ whiteSpace: "nowrap" }}>ROT</span>
                    </Tooltip>
                ),
                dataIndex: "rotation_no",
                key: "rotation_no",
                width: "5%",
                align: "center",
                render: (_, record) => {
                    if (record.isEmpty) return <EmptyCell />;
                    if (!record.flight) return <EmptyCell />;
                    return <RotationVisualizer flight={record.flight} allFlights={draftFlights || []} />;
                },
            },
            {
                title: (
                    <span style={{ whiteSpace: "nowrap" }}>
                        DEP/ARR{" "}
                        <Popover
                            content={
                                <div style={{ maxWidth: 420, fontSize: 13, lineHeight: 1.8 }}>
                                    <div style={{ fontWeight: "bold", marginBottom: 8, fontSize: 14 }}>订单号规则</div>
                                    <p style={{ margin: "0 0 8px" }}>
                                        首次发布时，系统按"轮换组"（Rotation）自动生成订单号。每个轮换组分配一个序号，代表本月第几个轮换组。去程（拉货）航班使用 CB 前缀（如 CB-2603-001），回程（定位）航班使用 CR 前缀（如 CR-2603-001），两者共享同一序号。
                                    </p>
                                    <p style={{ margin: "0 0 8px" }}>
                                        计划变更时，系统将新计划与当前执行中的航班匹配。匹配成功的航班，订单号自动从旧航班延续到新航班——无论时间、航路如何调整，只要是同一趟飞行任务，订单号不变。
                                    </p>
                                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                                        <li>航班替换：新航班直接继承旧航班的订单号</li>
                                        <li>航班拆分（如备降）：所有航段共享原航班订单号</li>
                                        <li>航班合并（如经停改直飞）：同一订单自动继承，不同订单需人工确认</li>
                                        <li>新增航班：归属已有轮换组则关联现有订单，全新轮换组则生成新订单号</li>
                                        <li>被取消/替换的航班：保留原订单号，用于成本归集</li>
                                    </ul>
                                </div>
                            }
                            trigger="hover"
                            placement="bottomLeft"
                        >
                            <QuestionCircleOutlined style={{ fontSize: 11, color: "#999", cursor: "help" }} />
                        </Popover>
                    </span>
                ),
                dataIndex: "airports",
                key: "airports",
                width: "12%",
                render: (_, record) => {
                    if (record.isEmpty) return <EmptyCell />;
                    // 右侧（新计划）：从匹配的执行航班继承 booking_no
                    // matchedFlight 指向匹配的执行航班（leftFlight），其中包含 booking_no
                    const inheritedBookingNo = record.matchedFlight?.booking_no;
                    // 新增航班（无匹配）显示 "-"，有匹配但无订单号则不显示
                    const displayBookingNo = record.diffType === DIFF_TYPE.ADDED ? "-" : inheritedBookingNo;
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
                            <div>{`${record.flight?.departure_airport || ""} → ${record.flight?.arrival_airport || ""}`}</div>
                            {displayBookingNo && (
                                <div style={{ fontSize: 11, color: '#999', lineHeight: '16px' }}>
                                    {displayBookingNo}
                                </div>
                            )}
                        </div>
                    );
                },
            },
            {
                title: "时间",
                dataIndex: "time",
                key: "time",
                align: "center",
                width: "14%",
                render: (_, record) => {
                    if (record.isEmpty) return <EmptyCell />;
                    const flight = record.flight;
                    const depAirport = flight?.departure_airport;
                    const arrAirport = flight?.arrival_airport;

                    return (
                        <div className="time-display-wrapper" style={{ display: "flex", alignItems: "center" }}>
                            <div>
                                <FlightTimeDisplay utcTime={flight?.std} airportCode={depAirport} airportsMap={airportsMap} />
                                <FlightTimeDisplay utcTime={flight?.sta} airportCode={arrAirport} airportsMap={airportsMap} />
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", fontSize: "12px", color: "#999", marginLeft: 12 }}>
                                <Tooltip
                                    title={
                                        <span>
                                            Block Hour: {formatMinutes(flight?.block_time_minutes)}
                                            <br />
                                            Ground Time: {formatMinutes(flight?.ground_time_minutes)}
                                        </span>
                                    }
                                >
                                    <div>{flight?.block_time_minutes ? `${formatMinutes(flight.block_time_minutes)}` : ""}</div>
                                    <div>{flight?.ground_time_minutes ? `${formatMinutes(flight.ground_time_minutes)}` : ""}</div>
                                </Tooltip>
                            </div>
                        </div>
                    );
                },
            },
            {
                title: "STC",
                dataIndex: "stc_type",
                key: "stc_type",
                width: "7%",
                render: (_, record) => {
                    if (record.isEmpty) return <EmptyCell />;
                    return record.flight?.stc_type || "";
                },
            },
            {
                title: (
                    <Tooltip title="匹配逻辑说明: 精确匹配(机场对相同且时间差<12小时)、模糊匹配(机场对相同且时间差12-48小时)、未匹配(其他情况)">
                        <span>
                            匹配类型 <InfoCircleOutlined style={{ fontSize: "14px", marginLeft: "4px" }} />
                        </span>
                    </Tooltip>
                ),
                dataIndex: "matchType",
                key: "matchType",
                width: "12%",
                render: (matchType, record) => {
                    if (record.isEmpty) return <EmptyCell />;

                    // 只在组的第一行显示匹配类型
                    if (!record.isGroupStart) {
                        return null;
                    }

                    // 检查是否有手动匹配
                    const hasManualMatch =
                        record.flight &&
                        manualMatchings.some((match) => {
                            if (!record.flight) return false;
                            const flightKey = `${record.flight.flight_number}_${record.flight.departure_airport}_${record.flight.arrival_airport}_${record.flight.std}`;
                            return (match.rightKeys && match.rightKeys.includes(flightKey)) || (match.rightIds && record.flight.id && match.rightIds.some((id) => String(id) === String(record.flight.id)));
                        });

                    // 匹配模式下的操作
                    if (matchingMode) {
                        // 判断当前行是否被选中
                        const isSelected = selectedRightRows.some((r) => r.rowKey === record.rowKey && r.subRowKey === record.subRowKey);

                        // 如果已有手动匹配，显示删除匹配按钮
                        if (hasManualMatch) {
                            const matchId = manualMatchings.find((match) => {
                                if (!record.flight) return false;
                                const flightKey = `${record.flight.flight_number}_${record.flight.departure_airport}_${record.flight.arrival_airport}_${record.flight.std}`;
                                return (match.rightKeys && match.rightKeys.includes(flightKey)) || (match.rightIds && record.flight.id && match.rightIds.some((id) => String(id) === String(record.flight.id)));
                            })?.id;

                            if (matchId) {
                                return (
                                    <Button
                                        danger
                                        icon={<DisconnectOutlined />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            deleteMatching(matchId);
                                        }}
                                    >
                                        解除匹配
                                    </Button>
                                );
                            }
                        }
                        // 如果已选择行且没有匹配关系，显示创建匹配按钮
                        else if (isSelected && selectedLeftRows.length > 0) {
                            return (
                                <Button
                                    type="primary"
                                    icon={<LinkOutlined />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        createMatching();
                                    }}
                                >
                                    创建匹配
                                </Button>
                            );
                        }
                        // 如果已选择行但还未选择左侧数据，提示用户
                        else if (isSelected) {
                            return <Tag color="orange">请先选择左侧行</Tag>;
                        }
                    }

                    // 根据不同匹配类型显示不同标签
                    let typeTag;
                    switch (matchType) {
                        case MATCH_TYPE.EXACT:
                            typeTag = <Tag color="blue">精确匹配</Tag>;
                            break;
                        case MATCH_TYPE.FUZZY:
                            typeTag = <Tag color="green">模糊匹配</Tag>;
                            break;
                        case MATCH_TYPE.UNMATCHED:
                            typeTag = <Tag color="red">未匹配</Tag>;
                            break;
                        case MATCH_TYPE.MANUAL:
                            typeTag = (
                                <Tag color="purple" icon={<LinkOutlined />}>
                                    手动匹配
                                </Tag>
                            );
                            break;
                        default:
                            typeTag = <Tag color="purple">合并匹配</Tag>;
                    }

                    const content = (
                        <div
                            style={{ position: "relative", minHeight: "24px" }}
                            onMouseEnter={(e) => {
                                const btnOverlay = e.currentTarget.querySelector(".hover-delete-overlay");
                                if (btnOverlay) btnOverlay.style.display = "flex";
                            }}
                            onMouseLeave={(e) => {
                                const btnOverlay = e.currentTarget.querySelector(".hover-delete-overlay");
                                if (btnOverlay) btnOverlay.style.display = "none";
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
                                <Tooltip title={`组内${record.totalInGroup}行`}>{typeTag}</Tooltip>
                            </div>

                            {/* 仅在非匹配模式下显示删除按钮，且仅对包含航班ID的记录显示 */}
                            {!matchingMode && contextAvailable && removeFlight && record.flight?.id && (
                                <div
                                    className="hover-delete-overlay"
                                    style={{
                                        display: "none",
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        zIndex: 1,
                                    }}
                                >
                                    <Popconfirm
                                        title="确定要删除该航班吗？"
                                        onConfirm={(e) => {
                                            e.stopPropagation();
                                            handleRemoveDraftFlight(record.flight.id);
                                        }}
                                        okText="是"
                                        cancelText="否"
                                    >
                                        <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} />
                                    </Popconfirm>
                                </div>
                            )}
                        </div>
                    );

                    return content;
                },
            },
        ],
        [EmptyCell, getCompleteChanges, getDiffTag, createMatching, deleteMatching, manualMatchings, matchingMode, renderChangesContent, selectedLeftRows, selectedRightRows, airportsMap, removeFlight, handleRemoveDraftFlight, contextAvailable, draftFlights],
    );

    // ========== Effect钩子 ==========
    // 每次draftFlights变更，如果对比面板可见，则重新生成对比结果
    useEffect(() => {
        if (compareVisible && currentOperation?.flights) {
            generateDiffResult(draftFlights, currentOperation.flights);
        }
        // 添加必要的依赖项
    }, [draftFlights, compareVisible, manualMatchings, generateDiffResult, currentOperation]);

    // 同步左右表格的滚动
    useEffect(() => {
        if (!compareVisible || !diffResult) return;

        // 使用setTimeout确保DOM已完全渲染
        const timer = setTimeout(() => {
            // 使用更精确的选择器获取表格体
            const leftTableBody = document.querySelector(".diff-tables-container .ant-col:nth-child(1) .ant-table-body");
            const rightTableBody = document.querySelector(".diff-tables-container .ant-col:nth-child(2) .ant-table-body");

            if (!leftTableBody || !rightTableBody) {
                console.warn("未找到表格滚动容器", { leftTableBody, rightTableBody });
                return;
            }

            const syncScroll = (e) => {
                const { scrollTop } = e.target;
                if (e.target === leftTableBody && rightTableBody.scrollTop !== scrollTop) {
                    rightTableBody.scrollTop = scrollTop;
                } else if (e.target === rightTableBody && leftTableBody.scrollTop !== scrollTop) {
                    leftTableBody.scrollTop = scrollTop;
                }
            };

            leftTableBody.addEventListener("scroll", syncScroll);
            rightTableBody.addEventListener("scroll", syncScroll);

            return () => {
                leftTableBody.removeEventListener("scroll", syncScroll);
                rightTableBody.removeEventListener("scroll", syncScroll);
            };
        }, 300); // 延迟300ms确保DOM已渲染

        return () => clearTimeout(timer);
    }, [compareVisible, diffResult]);

    // 处理详细的变更
    const generateComparisonDetails = useCallback(
        (currentDiffResult, currentManualMatchings) => {
            if (!currentDiffResult) return null;

            return {
                addedFlights: currentDiffResult.added.map((item) => ({
                    flight_number: item.draft.flight_number,
                    departure_airport: item.draft.departure_airport,
                    arrival_airport: item.draft.arrival_airport,
                    std: formatDateValue(item.draft.std) + " " + formatTimeValue(item.draft.std),
                    sta: formatDateValue(item.draft.sta) + " " + formatTimeValue(item.draft.sta),
                    draft: JSON.parse(JSON.stringify(item.draft)),
                })),
                removedOperations: currentDiffResult.removed.map((item) => ({
                    flight_number: item.operation.flight_number,
                    departure_airport: item.operation.departure_airport,
                    arrival_airport: item.operation.arrival_airport,
                    std: formatDateValue(item.operation.std) + " " + formatTimeValue(item.operation.std),
                    sta: formatDateValue(item.operation.sta) + " " + formatTimeValue(item.operation.sta),
                    id: item.operation.id,
                    operation: JSON.parse(JSON.stringify(item.operation)),
                })),
                changedPairs: currentDiffResult.changed.map((item) => ({
                    oldFlight: {
                        flight_number: item.operation.flight_number,
                        departure_airport: item.operation.departure_airport,
                        arrival_airport: item.operation.arrival_airport,
                        std: formatDateValue(item.operation.std) + " " + formatTimeValue(item.operation.std),
                        sta: formatDateValue(item.operation.sta) + " " + formatTimeValue(item.operation.sta),
                        id: item.operation.id,
                        operation: JSON.parse(JSON.stringify(item.operation)),
                    },
                    newFlight: {
                        flight_number: item.draft.flight_number,
                        departure_airport: item.draft.departure_airport,
                        arrival_airport: item.draft.arrival_airport,
                        std: formatDateValue(item.draft.std) + " " + formatTimeValue(item.draft.std),
                        sta: formatDateValue(item.draft.sta) + " " + formatTimeValue(item.draft.sta),
                        draft: JSON.parse(JSON.stringify(item.draft)),
                    },
                    changes: getFlightChanges(item.draft, item.operation),
                })),
                manualMatchings: currentManualMatchings.map((match) => ({
                    ...match,
                    id: match.id || `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                })),
                timestamp: new Date().toISOString(),
            };
        },
        [getFlightChanges, formatDateValue, formatTimeValue],
    );

    // 将对比结果保存到Context
    useEffect(() => {
        // 只在上下文可用时尝试保存结果 (使用新的标志)
        if (diffResult && Object.keys(diffResult).length > 0 && contextAvailable) {
            try {
                // 创建一个简化版的对比结果，只包含关键信息
                const simplifiedDiffResult = {
                    totalChanges: diffResult.totalChanges || 0,
                    added: diffResult.added?.length || 0,
                    removed: diffResult.removed?.length || 0,
                    changed: diffResult.changed?.length || 0,
                    unchanged: diffResult.unchanged?.length || 0,
                    flights: diffResult.flights?.length || 0,
                    timestamp: new Date().toISOString(),
                    manualMatchings: manualMatchings || [],
                    // 保存手动调整的执行航班（添加/删除的航班ID）
                    operationAdjustments: operationAdjustments || { addedIds: [], removedIds: [] },
                    summary: {
                        addedFlights: Array.isArray(diffResult.added)
                            ? diffResult.added
                                  .filter((f) => f && f.draft && f.draft.flight_number)
                                  .map((f) => ({
                                      flight_number: f.draft.flight_number,
                                      departure_airport: f.draft.departure_airport,
                                      arrival_airport: f.draft.arrival_airport,
                                      std: f.draft.std,
                                      sta: f.draft.sta,
                                  }))
                            : [],
                        removedFlights: Array.isArray(diffResult.removed)
                            ? diffResult.removed
                                  .filter((f) => f && f.operation && f.operation.flight_number)
                                  .map((f) => ({
                                      flight_number: f.operation.flight_number,
                                      departure_airport: f.operation.departure_airport,
                                      arrival_airport: f.operation.arrival_airport,
                                      std: f.operation.std,
                                      sta: f.operation.sta,
                                  }))
                            : [],
                        changedFlights: Array.isArray(diffResult.changed)
                            ? diffResult.changed
                                  .filter((item) => item && item.draft && item.draft.flight_number && item.operation)
                                  .map((item) => ({
                                      flight_number: item.draft.flight_number,
                                      changes: getFlightChanges(item.draft, item.operation),
                                  }))
                            : [],
                    },
                };

                // 只有在上下文可用且 updateCompareResult 存在时才更新上下文 (updateCompareResult 在 context 不可用时为 null)
                if (updateCompareResult) {
                    updateCompareResult(simplifiedDiffResult);
                }
            } catch (error) {
                console.error("保存对比结果到Context时出错:", error);
            }
        }
    }, [diffResult, manualMatchings, operationAdjustments, updateCompareResult, getFlightChanges, contextAvailable]);

    // 检查参数中是否有保存的对比结果并自动加载
    useEffect(() => {
        // 只在组件首次加载时执行一次初始化
        let isInitializing = true;

        // 如果有已保存的手动匹配数据或手动调整且有飞机信息，优先使用保存的数据初始化对比
        const savedResult = state?.parameters?.flight_compare_result;
        const hasManualMatchings = savedResult?.manualMatchings && Array.isArray(savedResult.manualMatchings) && savedResult.manualMatchings.length > 0;
        const hasOperationAdjustments = savedResult?.operationAdjustments && (savedResult.operationAdjustments.addedIds?.length > 0 || savedResult.operationAdjustments.removedIds?.length > 0);

        if (contextAvailable && (hasManualMatchings || hasOperationAdjustments) && schedule?.aircraft?.registration && !diffResult && !loading) {
            try {
                // 提取保存的手动匹配数据
                const savedMatchings = hasManualMatchings ? savedResult.manualMatchings : [];
                // 提取保存的手动调整数据
                const savedAdjustments = savedResult.operationAdjustments || { addedIds: [], removedIds: [] };

                // 使用保存的数据加载对比
                loadCurrentOperations(savedMatchings, savedAdjustments);
                setHasSavedResult(true);
                isInitializing = false;
            } catch (error) {
                console.error("处理保存的对比结果时出错:", error);
                setLoading(false);
            }
        }
        // 否则，如果有飞机信息但没有保存数据或处理失败，不自动加载
        else if (schedule?.aircraft?.registration && isInitializing) {
            setLoading(false);
            isInitializing = false;
        }
        // 如果只有保存结果（可能只有统计）但没有飞机信息，设置标志位等待后续处理
        else if (contextAvailable && savedResult && !diffResult) {
            setHasSavedResult(true);
        }
    }, [schedule?.aircraft?.registration, state?.parameters?.flight_compare_result, contextAvailable, diffResult, loading, loadCurrentOperations]); // 添加 contextAvailable 到依赖项

    // 将匹配关系变更通知给父组件
    useEffect(() => {
        if (onMatchingsChange && manualMatchings) {
            onMatchingsChange(manualMatchings);
        }
    }, [manualMatchings, onMatchingsChange]);

    // 将对比结果变更通知给父组件
    useEffect(() => {
        if (onCompareResult && diffResult) {
            // 传递完整的 diffResult 对象，而不是仅仅统计信息
            // 确保传递的是一个包含详细列表的对象
            const safeGetFlightChanges = (draft, op) => {
                try {
                    return getFlightChanges(draft, op);
                } catch (error) {
                    console.error("[FlightCompare] Error in getFlightChanges:", error, "Draft:", draft, "Op:", op);
                    return []; // 返回空数组以防出错
                }
            };

            const safeCreateFlightKey = (flight) => {
                try {
                    // 增加检查，确保 flight 对象和必要属性存在
                    if (flight && flight.flight_number && flight.departure_airport && flight.arrival_airport && flight.std) {
                        return createFlightKey(flight);
                    }
                    console.warn("[FlightCompare] Invalid flight object passed to safeCreateFlightKey:", flight);
                    return null; // 对于无效对象返回 null
                } catch (error) {
                    console.error("[FlightCompare] Error in safeCreateFlightKey:", error, "Flight:", flight);
                    return null;
                }
            };

            const completeResult = {
                // 使用 safeCreateFlightKey
                addedFlights:
                    diffResult.added
                        ?.filter((item) => item && item.draft) // 先过滤无效 item
                        .map((item) => safeCreateFlightKey(item.draft))
                        .filter((key) => key !== null) || [], // 过滤掉 null key
                removedOperations: diffResult.removed?.map((item) => item?.operation?.id).filter((id) => id !== undefined) || [],
                removedOperationsFullData: diffResult.removed?.map((item) => item?.operation).filter((operation) => operation !== undefined) || [], // 完整数据用于前端显示
                changedPairs:
                    diffResult.changed
                        ?.filter((item) => item && item.draft && item.operation) // 过滤无效 item
                        .map((item) => ({
                            old_operation_id: item.operation.id,
                            new_draft_key: safeCreateFlightKey(item.draft),
                            // 使用 safeGetFlightChanges
                            changes: safeGetFlightChanges(item.draft, item.operation),
                        }))
                        .filter((pair) => pair.old_operation_id !== undefined && pair.new_draft_key !== null) || [], // 过滤掉无效的 pair
                // 保留统计信息，如果父组件需要
                summary: {
                    totalChanges: diffResult.totalChanges || 0,
                    added: diffResult.added?.length || 0,
                    removed: diffResult.removed?.length || 0,
                    changed: diffResult.changed?.length || 0,
                    unchanged: diffResult.unchanged?.length || 0,
                },
            };

            // 调用回调函数传递完整结果
            onCompareResult(completeResult);
        }
    }, [diffResult, getFlightChanges]); // 保持依赖项，但注意 getFlightChanges 的稳定性

    // ========== 渲染函数 ==========
    // 在渲染部分的工具栏中添加加载按钮
    const renderToolbar = useCallback(
        () => (
            <Space>
                {compareVisible ? (
                    <>
                        {matchingMode ? (
                            <>
                                <Button type="primary" danger icon={<DisconnectOutlined />} onClick={toggleMatchingMode}>
                                    退出匹配模式
                                </Button>
                                <Button type="primary" icon={<LinkOutlined />} onClick={createMatching} disabled={selectedLeftRows.length === 0 && selectedRightRows.length === 0}>
                                    创建匹配
                                </Button>
                                <Button icon={<DisconnectOutlined />} onClick={resetAllMatchings} disabled={manualMatchings.length === 0}>
                                    重置匹配
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button type="primary" icon={<LinkOutlined />} onClick={toggleMatchingMode}>
                                    手动匹配
                                </Button>
                                <Button type="primary" ghost={true} icon={<SyncOutlined />} onClick={() => loadCurrentOperations()} loading={loading}>
                                    刷新对比
                                </Button>
                                {/* 执行航班手动调整按钮 */}
                                <Button
                                    icon={<PlusCircleOutlined />}
                                    onClick={() => {
                                        // 自动填充日期：取计划日期范围的前后3天
                                        // schedule 对象中使用 start_date 和 end_date 字段
                                        if (schedule?.start_date && schedule?.end_date) {
                                            const startDate = dayjs.utc(schedule.start_date).subtract(3, "day");
                                            const endDate = dayjs.utc(schedule.end_date).add(3, "day");

                                            // 使用 date_range 数组格式，与 UTCDateRangePicker 配合
                                            const newSearchParams = {
                                                date_range: [startDate, endDate],
                                            };
                                            setAddFlightSearchParams(newSearchParams);

                                            // 打开 modal 并自动触发搜索
                                            setAddFlightModalVisible(true);
                                            // 延迟执行搜索，确保 modal 已打开
                                            setTimeout(() => {
                                                searchFlightsToAdd(newSearchParams);
                                            }, 100);
                                        } else {
                                            // 如果没有日期范围，仅打开 modal
                                            setAddFlightModalVisible(true);
                                        }
                                    }}
                                >
                                    添加航班
                                </Button>
                                {(operationAdjustments.addedIds.length > 0 || operationAdjustments.removedIds.length > 0) && (
                                    <Tooltip title={`已添加 ${operationAdjustments.addedIds.length} 个，已移除 ${operationAdjustments.removedIds.length} 个`}>
                                        <Button icon={<SyncOutlined />} onClick={resetOperationAdjustments}>
                                            重置调整 ({operationAdjustments.addedIds.length + operationAdjustments.removedIds.length})
                                        </Button>
                                    </Tooltip>
                                )}
                                <Space>
                                    <Button icon={<SettingOutlined />} onClick={() => setSettingsModalVisible(true)}>
                                        匹配设置
                                        <InlineHelpButton contentPath="help/2025-7-23_flight-match-settings.md" title="航班匹配设置说明" tooltip="查看匹配设置详细说明" hideAfterViewed={true} />
                                    </Button>
                                </Space>
                                <Button icon={fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />} onClick={toggleFullscreen}>
                                    {fullscreen ? "退出全屏" : "全屏对比"}
                                </Button>
                            </>
                        )}
                    </>
                ) : (
                    <Button type="primary" icon={<DiffOutlined />} onClick={() => loadCurrentOperations()} loading={loading} disabled={!schedule?.aircraft?.registration}>
                        开始对比和匹配
                    </Button>
                )}
            </Space>
        ),
        [
            compareVisible,
            matchingMode,
            toggleMatchingMode,
            createMatching,
            selectedLeftRows.length,
            selectedRightRows.length,
            resetAllMatchings,
            manualMatchings.length,
            loadCurrentOperations,
            loading,
            fullscreen,
            toggleFullscreen,
            schedule?.aircraft?.registration,
            schedule?.start_date,
            schedule?.end_date,
            operationAdjustments,
            resetOperationAdjustments,
            searchFlightsToAdd,
        ],
    );

    // 渲染完整的对比概要面板
    const renderComparisonSummary = useCallback(() => {
        // 总是根据当前的 diffResult 和 manualMatchings 生成 comparisonDetails
        const comparisonDetails = generateComparisonDetails(diffResult, manualMatchings);

        if (!comparisonDetails) return null; // 如果无法生成，则不显示

        // 根据是否有数据来控制显示
        const hasAddedFlights = comparisonDetails.addedFlights?.length > 0;
        const hasRemovedOperations = comparisonDetails.removedOperations?.length > 0;
        const hasChangedPairs = comparisonDetails.changedPairs?.length > 0;
        const hasManualMatchings = comparisonDetails.manualMatchings?.length > 0;

        // 如果所有部分都没有数据，不显示概要
        if (!hasAddedFlights && !hasRemovedOperations && !hasChangedPairs && !hasManualMatchings) {
            return null;
        }

        // 切换展开/折叠状态的处理函数
        const toggleExpand = () => {
            setSummaryExpanded(!summaryExpanded);
        };

        // 判断需要显示的卡片数量，用于计算折叠状态时的宽度
        const cardCount = [hasAddedFlights, hasRemovedOperations, hasChangedPairs, hasManualMatchings].filter(Boolean).length;

        // 折叠状态下的简要概要
        if (!summaryExpanded) {
            return (
                <Card
                    title={
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span>
                                <InfoCircleOutlined /> 对比结果概要
                            </span>
                            <Button type="link" icon={<FullscreenOutlined />} onClick={toggleExpand} size="small">
                                展开详情
                            </Button>
                        </div>
                    }
                    style={{ marginBottom: 16 }}
                    size="small"
                    bordered={true}
                    className="comparison-summary-card comparison-summary-collapsed"
                >
                    <Row gutter={8} align="middle" justify="space-around">
                        {/* 简要统计信息 */}
                        {hasAddedFlights && (
                            <Col span={24 / cardCount}>
                                <div className="summary-stat">
                                    <PlusCircleOutlined style={{ color: "#52c41a", fontSize: 16 }} />
                                    <span style={{ marginLeft: 8 }}>
                                        新增航班: <Text strong>{comparisonDetails.addedFlights.length}</Text>
                                    </span>
                                </div>
                            </Col>
                        )}
                        {hasRemovedOperations && (
                            <Col span={24 / cardCount}>
                                <div className="summary-stat">
                                    <MinusCircleOutlined style={{ color: "#ff4d4f", fontSize: 16 }} />
                                    <span style={{ marginLeft: 8 }}>
                                        取消航班: <Text strong>{comparisonDetails.removedOperations.length}</Text>
                                    </span>
                                </div>
                            </Col>
                        )}
                        {hasChangedPairs && (
                            <Col span={24 / cardCount}>
                                <div className="summary-stat">
                                    <EditOutlined style={{ color: "#faad14", fontSize: 16 }} />
                                    <span style={{ marginLeft: 8 }}>
                                        变更航班: <Text strong>{comparisonDetails.changedPairs.length}</Text>
                                    </span>
                                </div>
                            </Col>
                        )}
                        {hasManualMatchings && (
                            <Col span={24 / cardCount}>
                                <div className="summary-stat">
                                    <LinkOutlined style={{ color: "#722ed1", fontSize: 16 }} />
                                    <span style={{ marginLeft: 8 }}>
                                        手动匹配: <Text strong>{comparisonDetails.manualMatchings.length}</Text>
                                    </span>
                                </div>
                            </Col>
                        )}
                    </Row>
                </Card>
            );
        }

        // 展开状态下的详细概要
        return (
            <Card
                title={
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span>
                            <InfoCircleOutlined /> 对比结果详情
                        </span>
                        <Button type="link" icon={<FullscreenExitOutlined />} onClick={toggleExpand} size="small">
                            收起详情
                        </Button>
                    </div>
                }
                style={{ marginBottom: 16 }}
                size="small"
                bordered={true}
                className="comparison-summary-card comparison-summary-expanded"
            >
                <Row gutter={[16, 16]} align="top">
                    {/* 新增航班部分 */}
                    {hasAddedFlights && (
                        <Col span={hasChangedPairs || hasRemovedOperations || hasManualMatchings ? 12 : 24}>
                            <Card
                                title={
                                    <>
                                        <PlusCircleOutlined style={{ color: "#52c41a" }} /> 新增航班 ({comparisonDetails.addedFlights.length})
                                    </>
                                }
                                size="small"
                                bodyStyle={{ maxHeight: "550px", overflow: "auto" }}
                            >
                                {comparisonDetails.addedFlights.map((flight, index) => (
                                    <div key={`added-${index}`} style={{ marginBottom: 8, padding: "4px 8px", background: "rgba(82, 196, 26, 0.1)", borderRadius: 4 }}>
                                        <div>
                                            <strong>{flight.flight_number}</strong> · {flight.departure_airport} → {flight.arrival_airport}
                                        </div>
                                        <div style={{ fontSize: "12px", color: "#888" }}>
                                            起飞: {flight.std} | 到达: {flight.sta}
                                        </div>
                                    </div>
                                ))}
                            </Card>
                        </Col>
                    )}

                    {/* 取消航班部分 */}
                    {hasRemovedOperations && (
                        <Col span={hasAddedFlights || hasChangedPairs || hasManualMatchings ? 12 : 24}>
                            <Card
                                title={
                                    <>
                                        <MinusCircleOutlined style={{ color: "#ff4d4f" }} /> 取消航班 ({comparisonDetails.removedOperations.length})
                                    </>
                                }
                                size="small"
                                bodyStyle={{ maxHeight: "550px", overflow: "auto" }}
                            >
                                {comparisonDetails.removedOperations.map((flight, index) => (
                                    <div key={`removed-${index}`} style={{ marginBottom: 8, padding: "4px 8px", background: "rgba(255, 77, 79, 0.1)", borderRadius: 4 }}>
                                        <div>
                                            <strong>{flight.flight_number}</strong> · {flight.departure_airport} → {flight.arrival_airport}
                                        </div>
                                        <div style={{ fontSize: "12px", color: "#888" }}>
                                            起飞: {pickEditorDepartureTime(flight) || ""} | 到达: {pickEditorArrivalTime(flight) || ""}
                                        </div>
                                    </div>
                                ))}
                            </Card>
                        </Col>
                    )}

                    {/* 变更航班部分 */}
                    {hasChangedPairs && (
                        <Col span={hasAddedFlights || hasRemovedOperations || hasManualMatchings ? 12 : 24}>
                            <Card
                                title={
                                    <>
                                        <EditOutlined style={{ color: "#faad14" }} /> 航班变更 ({comparisonDetails.changedPairs.length})
                                    </>
                                }
                                size="small"
                                bodyStyle={{ maxHeight: "550px", overflow: "auto" }}
                            >
                                {comparisonDetails.changedPairs.map((pair, index) => (
                                    <div key={`changed-${index}`} style={{ marginBottom: 8, padding: "4px 8px", background: "rgba(250, 173, 20, 0.1)", borderRadius: 4 }}>
                                        <div>
                                            <strong>{pair.newFlight.flight_number}</strong> · {pair.newFlight.departure_airport} → {pair.newFlight.arrival_airport}
                                        </div>
                                        <div style={{ fontSize: "12px" }}>
                                            {pair.changes.map((change, cIdx) => {
                                                // 时间变更需要特殊处理显示
                                                const isTimeField = change.field.includes("time") || change.field.includes("std") || change.field.includes("sta");
                                                const timeDiff = isTimeField ? calculateTimeDifference(change.oldValue, change.newValue) : null;

                                                return (
                                                    <div key={`change-${cIdx}`} style={{ marginTop: 2 }}>
                                                        <span style={{ color: "#666" }}>{change.fieldLabel}: </span>
                                                        <Text delete type="danger" style={{ fontSize: "11px" }}>
                                                            {change.isDateField ? change.oldDateOnly : isTimeField ? formatTimeValue(change.oldValue) : change.oldValue}
                                                        </Text>
                                                        <span style={{ margin: "0 4px" }}>→</span>
                                                        <Text type="success" style={{ fontSize: "11px" }}>
                                                            {change.isDateField ? change.newDateOnly : isTimeField ? formatTimeValue(change.newValue) : change.newValue}
                                                            {timeDiff && !change.isDateField && (
                                                                <span
                                                                    style={{
                                                                        marginLeft: 4,
                                                                        fontSize: "10px",
                                                                        padding: "0px 3px",
                                                                        backgroundColor: timeDiff.startsWith("+") ? "rgba(82, 196, 26, 0.2)" : "rgba(255, 77, 79, 0.2)",
                                                                        color: timeDiff.startsWith("+") ? "#52c41a" : "#ff4d4f",
                                                                        borderRadius: 2,
                                                                    }}
                                                                >
                                                                    {timeDiff}
                                                                </span>
                                                            )}
                                                        </Text>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </Card>
                        </Col>
                    )}

                    {/* 手动匹配关系部分 */}
                    {hasManualMatchings && (
                        <Col span={hasAddedFlights || hasRemovedOperations || hasChangedPairs ? 12 : 24}>
                            <Card
                                title={
                                    <>
                                        <LinkOutlined style={{ color: "#722ed1" }} /> 手动匹配关系 ({comparisonDetails.manualMatchings.length})
                                    </>
                                }
                                size="small"
                                bodyStyle={{ maxHeight: "550px", overflow: "auto" }}
                            >
                                {comparisonDetails.manualMatchings.map((matching, index) => {
                                    // 找到对应的左侧航班信息
                                    const leftFlights = currentOperation?.flights?.filter((f) => matching.leftIds.some((id) => String(id) === String(f.id))) || [];

                                    // 找到对应的右侧航班信息
                                    const rightFlights = draftFlights?.filter((f) => matching.rightKeys.some((key) => key === createFlightKey(f))) || [];

                                    let matchTypeLabel = "手动匹配";
                                    let matchTypeColor = "#722ed1";

                                    if (matching.type === MANUAL_MATCH_TYPE.SPLIT) {
                                        matchTypeLabel = "拆分";
                                        matchTypeColor = "#eb2f96";
                                    } else if (matching.type === MANUAL_MATCH_TYPE.MERGE) {
                                        matchTypeLabel = "合并";
                                        matchTypeColor = "#1890ff";
                                    }

                                    return (
                                        <div key={`matching-${index}`} style={{ marginBottom: 8, padding: "4px 8px", background: "rgba(114, 46, 209, 0.1)", borderRadius: 4 }}>
                                            <div>
                                                <Tag color={matchTypeColor}>{matchTypeLabel}</Tag>
                                                <span style={{ fontSize: "12px" }}>
                                                    {leftFlights.length} 个原航班 → {rightFlights.length} 个新航班
                                                </span>
                                            </div>
                                            <div style={{ fontSize: "12px", color: "#666", marginTop: 2 }}>
                                                <span>原航班: </span>
                                                {leftFlights.map((f, i) => (
                                                    <Tag key={`left-${i}`} size="small" style={{ marginRight: 2 }}>
                                                        {f.flight_number}
                                                    </Tag>
                                                ))}
                                            </div>
                                            <div style={{ fontSize: "12px", color: "#666" }}>
                                                <span>新航班: </span>
                                                {rightFlights.map((f, i) => (
                                                    <Tag key={`right-${i}`} size="small" style={{ marginRight: 2 }}>
                                                        {f.flight_number}
                                                    </Tag>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </Card>
                        </Col>
                    )}
                </Row>
            </Card>
        );
    }, [diffResult, manualMatchings, getFlightChanges, contextAvailable, state?.parameters?.flight_compare_result, generateComparisonDetails, summaryExpanded]);

    // 组件主渲染
    return (
        <div className={`flight-compare-container ${fullscreen ? "fullscreen" : ""}`}>
            <Card
                title={
                    <Space>
                        <DiffOutlined /> 匹配执行计划
                    </Space>
                }
                extra={renderToolbar()}
                bordered={false}
                style={{ width: "100%" }}
                className={fullscreen ? "diff-fullscreen-container" : "compare-card-compact"}
            >
                {compareVisible ? (
                    <>
                        <div className="diff-header">
                            <div className="diff-title">
                                <DiffOutlined style={{ marginRight: 8 }} />
                                <span>对比结果：</span>
                                {schedule?.aircraft?.registration && <AircraftTag registration={schedule.aircraft.registration} />}
                                {contextAvailable && state.parameters?.flight_compare_result && diffResult?.loaded && (
                                    <Tag color="cyan" style={{ marginLeft: 8 }}>
                                        保存的对比结果
                                    </Tag>
                                )}
                                {matchingMode && (
                                    <Tag color="purple" style={{ marginLeft: 8 }}>
                                        <LinkOutlined /> 手动匹配模式
                                    </Tag>
                                )}
                            </div>
                            <div className="diff-stats">{renderStats()}</div>
                        </div>

                        {/* 智能匹配说明 */}
                        {!matchingMode && diffResult && (
                            <Alert
                                message={matchingSettings.matchingStrategy === "sequential" ? "智能顺序匹配机制" : "智能最优匹配机制"}
                                description={
                                    <div>
                                        {matchingSettings.matchingStrategy === "sequential" ? (
                                            <>
                                                <p>系统采用航班号优先 + 顺序配对匹配算法：</p>
                                                <ul style={{ marginBottom: 8, paddingLeft: 20 }}>
                                                    <li>
                                                        <strong>航班号优先：</strong>相同航班号且相同机场对的航班先配。有人争时（一边一班、另一边两班或更多也算）按起飞更近的一对一配，已配上的不再给别人
                                                    </li>
                                                    <li>
                                                        <strong>顺序配对：</strong>本组没配上的、以及航班号或航段不同的剩余班，按时间顺序依次配对
                                                    </li>
                                                    <li>
                                                        <strong>时间标记：</strong>时间差只决定精确 / 模糊标签；有人争时谁留下不看阈值。没人争时差几天仍可配（改期）
                                                    </li>
                                                    <li>
                                                        <strong>策略范围：</strong>对照页上的「最优匹配」改不了第一轮已经占走的班
                                                    </li>
                                                </ul>
                                                <div style={{ fontSize: "12px", color: "#666" }}>💡 提示：适用于航班号不变的延误场景。一周里同一航班号飞两次时，更近的那对会留下</div>
                                            </>
                                        ) : (
                                            <>
                                                <p>系统采用航班号优先 + 最优匹配算法：</p>
                                                <ul style={{ marginBottom: 8, paddingLeft: 20 }}>
                                                    <li>
                                                        <strong>航班号优先：</strong>相同航班号且相同机场对的航班先配。有人争时按起飞更近的一对一配，已配上的不再给别人
                                                    </li>
                                                    <li>
                                                        <strong>最优选择：</strong>本组没配上的剩余班，再为每个新编航班寻找时间差最小的执行航班
                                                    </li>
                                                    <li>
                                                        <strong>策略范围：</strong>「最优匹配」只处理第一轮剩下的班，改不了航班号相同且航段相同这一组已经占走的班
                                                    </li>
                                                    <li>
                                                        <strong>时间标记：</strong>时间差只决定精确 / 模糊标签；没人争时差几天仍可配（改期）
                                                    </li>
                                                </ul>
                                                <div style={{ fontSize: "12px", color: "#666" }}>💡 提示：适用于航班号可能变更的场景。同一航班号有人争时，两种策略结果相同</div>
                                            </>
                                        )}
                                    </div>
                                }
                                type="info"
                                showIcon
                                closable
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        {/* 插入对比结果概要面板 */}
                        {!matchingMode && diffResult && diffResult.totalChanges > 0 && renderComparisonSummary()}

                        {matchingMode && (
                            <Alert
                                message="匹配模式已启用"
                                description={
                                    <div>
                                        <p>在左右表格中点击行来选择需要匹配的航班，然后点击"创建匹配"按钮建立关联。</p>
                                        <p>
                                            已选择：左侧 <Tag color="blue">{selectedLeftRows.length}</Tag> 行，右侧 <Tag color="blue">{selectedRightRows.length}</Tag> 行
                                        </p>
                                        <p>
                                            已创建 <Tag color="purple">{manualMatchings.length}</Tag> 个手动匹配
                                        </p>
                                    </div>
                                }
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        {loading ? (
                            <div style={{ padding: "40px 0", textAlign: "center" }}>
                                <Spin tip="加载对比数据中..." />
                            </div>
                        ) : error ? (
                            <Alert message="错误" description={error} type="error" showIcon />
                        ) : !diffResult?.flights || diffResult.flights.length === 0 ? (
                            <Empty description="没有可对比的航班数据" />
                        ) : (
                            <>
                                <div className="diff-tables-container">
                                    <Row>
                                        <Col span={12} className="table-col">
                                            <div className="table-header">当前执行计划</div>
                                            <Table
                                                dataSource={diffResult.leftData}
                                                columns={leftColumns}
                                                pagination={false}
                                                size="small"
                                                rowKey={(record) => `left_${record.rowKey}_${record.subRowKey}`}
                                                rowClassName={(record) => {
                                                    // 基础样式
                                                    let className = getRowClassName(record);

                                                    // 匹配模式下的选中状态
                                                    if (matchingMode && selectedLeftRows.some((r) => r.rowKey === record.rowKey && r.subRowKey === record.subRowKey)) {
                                                        className += " selected-row";
                                                    }

                                                    // 标记手动匹配的行
                                                    const hasManualMatch = manualMatchings.some((match) => {
                                                        if (!record.flight || !currentOperation) return false;
                                                        return match.leftIds && record.flight.id && match.leftIds.includes(record.flight.id);
                                                    });

                                                    if (hasManualMatch) {
                                                        className += " manual-match-row";
                                                    }

                                                    return className;
                                                }}
                                                onRow={(record) => ({
                                                    onClick: () => matchingMode && handleRowSelection(record, "left"),
                                                    onMouseEnter: () => {
                                                        // 高亮对应组的所有行
                                                        if (!matchingMode) {
                                                            const groupRows = document.querySelectorAll(`.group-${record.groupId}`);
                                                            groupRows.forEach((row) => row.classList.add("row-hover"));
                                                        }
                                                    },
                                                    onMouseLeave: () => {
                                                        // 移除高亮
                                                        if (!matchingMode) {
                                                            const rows = document.querySelectorAll(`.row-hover`);
                                                            rows.forEach((row) => row.classList.remove("row-hover"));
                                                        }
                                                    },
                                                })}
                                                sticky={{ offsetHeader: 0 }} // 固定表头
                                            />
                                        </Col>
                                        <Col span={12} className="table-col">
                                            <div className="table-header">编辑中计划</div>
                                            <Table
                                                dataSource={diffResult.rightData}
                                                columns={rightColumns}
                                                pagination={false}
                                                size="small"
                                                rowKey={(record) => `right_${record.rowKey}_${record.subRowKey}`}
                                                rowClassName={(record) => {
                                                    // 基础样式
                                                    let className = getRowClassName(record);

                                                    // 匹配模式下的选中状态
                                                    if (matchingMode && selectedRightRows.some((r) => r.rowKey === record.rowKey && r.subRowKey === record.subRowKey)) {
                                                        className += " selected-row";
                                                    }

                                                    // 标记手动匹配的行
                                                    const hasManualMatch = manualMatchings.some((match) => {
                                                        if (!record.flight || !draftFlights) return false;
                                                        const flightKey = `${record.flight.flight_number}_${record.flight.departure_airport}_${record.flight.arrival_airport}_${record.flight.std}`;
                                                        return (match.rightKeys && match.rightKeys.includes(flightKey)) || (match.rightIds && record.flight.id && match.rightIds.some((id) => String(id) === String(record.flight.id)));
                                                    });

                                                    if (hasManualMatch) {
                                                        className += " manual-match-row";
                                                    }

                                                    return className;
                                                }}
                                                onRow={(record) => ({
                                                    onClick: () => matchingMode && handleRowSelection(record, "right"),
                                                    onMouseEnter: () => {
                                                        // 高亮对应组的所有行
                                                        if (!matchingMode) {
                                                            const groupRows = document.querySelectorAll(`.group-${record.groupId}`);
                                                            groupRows.forEach((row) => row.classList.add("row-hover"));
                                                        }
                                                    },
                                                    onMouseLeave: () => {
                                                        // 移除高亮
                                                        if (!matchingMode) {
                                                            const rows = document.querySelectorAll(`.row-hover`);
                                                            rows.forEach((row) => row.classList.remove("row-hover"));
                                                        }
                                                    },
                                                })}
                                                sticky={{ offsetHeader: 0 }}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            </>
                        )}
                    </>
                ) : (
                    <div style={{ display: "flex", alignItems: "center", padding: "8px 12px", background: "#f9f9f9", borderRadius: "2px" }}>
                        <span style={{ color: "#666" }}>{schedule?.aircraft?.registration ? "点击上方按钮与执行计划进行对比" : "请先在基本信息中选择执行飞机才能进行对比"}</span>
                        {!schedule?.aircraft?.registration && (
                            <Button type="link" size="small" onClick={() => document.querySelector(".schedule-basic-info")?.scrollIntoView({ behavior: "smooth" })}>
                                前往选择飞机
                            </Button>
                        )}
                    </div>
                )}

                {/* 手动匹配模式对话框 */}
                <Modal title="创建匹配关系" open={matchingModalVisible} onOk={confirmCreateMatching} onCancel={() => setMatchingModalVisible(false)} okText="确认匹配" cancelText="取消">
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontWeight: "bold", marginBottom: 8 }}>已选择:</div>
                        <div style={{ marginBottom: 8 }}>
                            左表:{" "}
                            {selectedLeftRows.length === 0
                                ? "无"
                                : selectedLeftRows.map((row) => {
                                      const flight = row.flight;
                                      if (!flight) return null;
                                      return (
                                          <Tag key={`left_${row.rowKey}_${row.subRowKey}`}>
                                              #{row.rowKey + 1} {flight.departure_airport}→{flight.arrival_airport} ({flight.flight_number})
                                          </Tag>
                                      );
                                  })}
                        </div>
                        <div>
                            右表:{" "}
                            {selectedRightRows.length === 0
                                ? "无"
                                : selectedRightRows.map((row) => {
                                      const flight = row.flight;
                                      if (!flight) return null;
                                      return (
                                          <Tag key={`right_${row.rowKey}_${row.subRowKey}`}>
                                              #{row.rowKey + 1} {flight.departure_airport}→{flight.arrival_airport} ({flight.flight_number})
                                          </Tag>
                                      );
                                  })}
                        </div>
                    </div>

                    <Divider />

                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontWeight: "bold", marginBottom: 8 }}>匹配类型:</div>
                        <Radio.Group value={currentMatchingType} onChange={(e) => setCurrentMatchingType(e.target.value)}>
                            <Radio value={MANUAL_MATCH_TYPE.MANUAL}>手动匹配</Radio>
                            <Radio value={MANUAL_MATCH_TYPE.SPLIT}>航线拆分</Radio>
                            <Radio value={MANUAL_MATCH_TYPE.MERGE}>航线合并</Radio>
                            <Radio value={MANUAL_MATCH_TYPE.OTHER}>其他</Radio>
                        </Radio.Group>
                    </div>

                    <div>
                        <Checkbox defaultChecked>记录成本分摊关系</Checkbox>
                    </div>
                </Modal>

                <style jsx>{`
                    .diff-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 16px;
                    }

                    .diff-title {
                        font-size: 16px;
                        font-weight: bold;
                        display: flex;
                        align-items: center;
                    }

                    .diff-tables-container {
                        border: 1px solid #f0f0f0;
                        border-radius: 2px;
                    }

                    .table-col {
                        padding: 0;
                    }

                    .table-col:first-child {
                        border-right: 1px solid #f0f0f0;
                    }

                    .table-header {
                        background: #fafafa;
                        padding: 12px 16px;
                        font-weight: bold;
                        border-bottom: 1px solid #f0f0f0;
                    }

                    /* 行样式 - 已禁用背景色，只保留标签颜色 */
                    /*
                    .diff-row-added {
                        background-color: rgba(82, 196, 26, 0.15);
                    }
                    .diff-row-added:hover td {
                        background-color: rgba(82, 196, 26, 0.3) !important;
                    }
                    .diff-row-removed {
                        background-color: rgba(245, 34, 45, 0.15);
                    }
                    .diff-row-removed:hover td {
                        background-color: rgba(245, 34, 45, 0.3) !important;
                    }
                    .diff-row-changed {
                        background-color: rgba(250, 173, 20, 0.15);
                    }
                    .diff-row-changed:hover td {
                        background-color: rgba(250, 173, 20, 0.3) !important;
                    }
                    */

                    /* 分组样式 */
                    .group-start-row td {
                        border-top: 1px solid #d9d9d9;
                    }

                    .group-end-row td {
                        border-bottom: 1px solid #d9d9d9;
                    }

                    /* 组内行之间的连接线 */
                    tr:not(.group-start-row):not(.group-end-row) td {
                        border-top: none;
                    }

                    /* 组之间的间距 */
                    .group-end-row + tr:not(.group-start-row) td {
                        border-top: 4px solid #f0f0f0;
                    }

                    /* 表格行高度一致性调整 */
                    .ant-table-tbody > tr {
                        height: 58px; /* 统一行高 */
                    }

                    .ant-table-tbody > tr > td {
                        vertical-align: middle; /* 垂直居中 */
                        padding: 8px 16px !important; /* 统一内边距 */
                    }

                    /* 所有单元格内容垂直居中 */
                    .ant-table-tbody > tr > td > div {
                        display: flex;
                        align-items: center;
                        min-height: 40px;
                    }

                    /* 空单元格样式 */
                    .empty-cell {
                        min-height: 44px;
                        height: 100%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex-direction: column;
                    }

                    .empty-cell-content {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        height: 22px;
                        width: 100%;
                    }

                    .empty-cell-spacer {
                        display: inline-block;
                        width: 100%;
                        height: 22px; /* 保持与文本行高一致 */
                        visibility: hidden;
                    }

                    .placeholder-cell {
                        background-color: #fafafa;
                        opacity: 0.5;
                    }

                    /* 空行样式 */
                    .empty-row td {
                        background-color: #fafafa;
                        opacity: 0.7;
                    }

                    /* 同步高亮样式 */
                    .row-hover td {
                        background-color: #e6f7ff !important;
                    }

                    /* 全屏样式 */
                    .diff-fullscreen-container {
                        position: fixed;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background-color: #fff;
                        z-index: 1000;
                        padding: 20px;
                        overflow: auto;
                    }

                    /* 设置空行的占位符高度 */
                    .ant-table-tbody > tr > td div.ant-empty-normal {
                        margin: 0;
                        padding: 8px 0;
                    }

                    /* 紧凑卡片样式 */
                    .compare-card-compact {
                        margin-bottom: 8px;
                    }
                    .compare-card-compact .ant-card-head {
                        min-height: 40px;
                    }
                    .compare-card-compact .ant-card-head-title,
                    .compare-card-compact .ant-card-extra {
                        padding: 8px 0;
                    }

                    /* 手动匹配模式样式 */
                    .matching-mode .ant-table-row {
                        cursor: pointer;
                    }

                    .selected-row td {
                        background-color: #bae0ff !important; /* 使用更深的蓝色 */
                        font-weight: 500; /* 稍微加粗字体 */
                    }

                    .manual-match-row td {
                        background-color: #f0f5ff !important;
                    }

                    .manual-match-row:hover td {
                        background-color: #d6e4ff !important;
                    }

                    /* 手动匹配标记样式 */
                    .manual-matching-tag {
                        border: 1px dashed #1890ff;
                    }

                    /* 时间显示组件样式修复 */
                    .ant-table-tbody > tr > td .time-display-wrapper {
                        display: flex;
                        align-items: center;
                        min-height: 44px;
                    }

                    .ant-table-tbody > tr > td .time-display-wrapper > div {
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                    }

                    /* 状态徽章样式 */
                    .status-badge .ant-badge-status-text {
                        margin-left: 6px; /* 调整文本和点的间距 */
                    }

                    /* 差异标签竖向排列 */
                    .diff-tag-with-info {
                        display: flex;
                        flex-direction: column;
                        align-items: flex-start;
                        width: 100%;
                    }

                    .diff-tag-with-info .ant-tag {
                        margin-bottom: 4px;
                        display: inline-flex;
                        align-items: center;
                    }

                    /* 修复父容器问题 */
                    .ant-table-tbody > tr > td > div > div.diff-tag-with-info {
                        flex-direction: column;
                        align-items: flex-start !important;
                    }
                `}</style>

                {/* 匹配设置模态框 */}
                <Modal
                    title={
                        <Space>
                            <SettingOutlined />
                            航班匹配设置
                        </Space>
                    }
                    open={settingsModalVisible}
                    onCancel={() => setSettingsModalVisible(false)}
                    footer={<Button onClick={() => setSettingsModalVisible(false)}>关闭</Button>}
                    width={500}
                >
                    <div style={{ marginBottom: 16 }}>
                        <Alert
                            message={matchingSettings.matchingStrategy === "sequential" ? "顺序匹配策略说明" : "最优匹配策略说明"}
                            description={
                                <div>
                                    {matchingSettings.matchingStrategy === "sequential" ? (
                                        <>
                                            <p>系统采用航班号优先 + 顺序配对匹配算法：</p>
                                            <ul style={{ marginBottom: 8, paddingLeft: 20 }}>
                                                <li>
                                                    <strong>航班号优先：</strong>相同航班号且相同机场对先配。有人争时按起飞更近的一对一配
                                                </li>
                                                <li>
                                                    <strong>顺序配对：</strong>本组没配上的剩余班，按时间顺序依次配对
                                                </li>
                                                <li>
                                                    <strong>时间标记：</strong>时间差只决定精确 / 模糊标签，不决定有人争时谁留下
                                                </li>
                                                <li>
                                                    <strong>适用场景：</strong>航班号不变的大面积延误
                                                </li>
                                            </ul>
                                        </>
                                    ) : (
                                        <>
                                            <p>系统采用航班号优先 + 最优匹配算法：</p>
                                            <ul style={{ marginBottom: 8, paddingLeft: 20 }}>
                                                <li>
                                                    <strong>航班号优先：</strong>相同航班号且相同机场对先配。有人争时按起飞更近的一对一配
                                                </li>
                                                <li>
                                                    <strong>全局搜索：</strong>本组没配上的剩余班，再寻找时间差最小的匹配
                                                </li>
                                                <li>
                                                    <strong>策略范围：</strong>最优匹配改不了第一轮已经占走的班
                                                </li>
                                                <li>
                                                    <strong>适用场景：</strong>航线可能变更的场景
                                                </li>
                                            </ul>
                                        </>
                                    )}
                                    <p>
                                        <strong>匹配规则：</strong>
                                    </p>
                                    <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                                        <li>航班号相同且航段相同：有人争时按更近的配；没人争时差几天仍可配（改期）</li>
                                        <li>对照页上的「最优匹配」改不了第一轮已经占走的班</li>
                                        <li>精确匹配 / 模糊匹配：时间差只贴标签，默认大约 24 小时内为精确</li>
                                        <li>后两轮里超出模糊阈值的班不会自动配，需要手动处理。手动匹配仍优先，自动争用不得拆开</li>
                                    </ul>
                                </div>
                            }
                            type="info"
                            showIcon
                            style={{ marginBottom: 20 }}
                        />
                    </div>

                    <Form layout="vertical">
                        <Form.Item label="匹配策略">
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <Select value={matchingSettings.matchingStrategy} onChange={(value) => setMatchingSettings((prev) => ({ ...prev, matchingStrategy: value }))} style={{ width: 200 }}>
                                    <Select.Option value="sequential">顺序匹配（推荐）</Select.Option>
                                    <Select.Option value="optimal">最优匹配</Select.Option>
                                </Select>
                                <span style={{ color: "#666", fontSize: "12px" }}>{matchingSettings.matchingStrategy === "sequential" ? "有人争时按更近的配；剩下的班按顺序配对，适合大面积延误" : "有人争时按更近的配；剩下的班再找时间差最小的。最优匹配改不了第一轮已占走的班"}</span>
                            </div>
                        </Form.Item>

                        <Form.Item label="精确匹配时间阈值（小时）">
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <InputNumber value={matchingSettings.exactMatchThreshold} onChange={(value) => setMatchingSettings((prev) => ({ ...prev, exactMatchThreshold: value || 24 }))} style={{ width: 120 }} min={1} max={168} placeholder="24" />
                                <span style={{ color: "#666", fontSize: "12px" }}>时间差小于此值的航班将被标记为精确匹配</span>
                            </div>
                        </Form.Item>

                        <Form.Item label="模糊匹配时间阈值（小时）">
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <InputNumber value={matchingSettings.fuzzyMatchThreshold} onChange={(value) => setMatchingSettings((prev) => ({ ...prev, fuzzyMatchThreshold: value || 120 }))} style={{ width: 120 }} min={1} max={336} placeholder="72" />
                                <span style={{ color: "#666", fontSize: "12px" }}>时间差小于此值但大于精确匹配阈值的航班将被标记为模糊匹配</span>
                            </div>
                        </Form.Item>
                    </Form>
                </Modal>

                {/* 添加航班模态框 */}
                <Modal
                    title={
                        <Space>
                            <PlusCircleOutlined />
                            添加执行航班到对比列表
                        </Space>
                    }
                    className="large-modal"
                    open={addFlightModalVisible}
                    onCancel={() => {
                        setAddFlightModalVisible(false);
                        setAddFlightSearchResults([]);
                    }}
                    footer={<Button onClick={() => setAddFlightModalVisible(false)}>关闭</Button>}
                    width={900}
                >
                    <Form layout="inline" style={{ marginBottom: 16 }}>
                        <Form.Item label="日期范围 (UTC)">
                            <UTCDateRangePicker value={addFlightSearchParams.date_range} onChange={(dates) => setAddFlightSearchParams((prev) => ({ ...prev, date_range: dates }))} placeholder={["开始日期 (UTC)", "结束日期 (UTC)"]} style={{ width: 280 }} />
                        </Form.Item>
                        <Form.Item>
                            <Button type="primary" icon={<SearchOutlined />} onClick={() => searchFlightsToAdd(addFlightSearchParams)} loading={addFlightLoading}>
                                搜索
                            </Button>
                        </Form.Item>
                    </Form>

                    <Table
                        dataSource={addFlightSearchResults}
                        rowKey="id"
                        size="small"
                        loading={addFlightLoading}
                        scroll={{ y: 400 }}
                        pagination={false}
                        columns={[
                            {
                                title: "日期",
                                dataIndex: "flight_date",
                                key: "flight_date",
                                width: 100,
                                render: (val) => (val ? dayjs.utc(val).format("MM-DD") : ""),
                            },
                            {
                                title: "航班号",
                                dataIndex: "flight_number",
                                key: "flight_number",
                                width: 100,
                            },
                            {
                                title: "航线",
                                key: "route",
                                width: 120,
                                render: (_, record) => `${record.departure_airport} → ${record.arrival_airport}`,
                            },
                            {
                                title: "时间",
                                key: "time",
                                width: 120,
                                render: (_, record) => {
                                    // 编辑器口径：ETD 优先、回退 STD；ETA 优先、回退 STA。跟左侧对比页保持一致。
                                    const dep = pickEditorDepartureTime(record);
                                    const arr = pickEditorArrivalTime(record);
                                    return (
                                        <span>
                                            {dep ? dayjs.utc(dep).format("HH:mm") : ""} - {arr ? dayjs.utc(arr).format("HH:mm") : ""}
                                        </span>
                                    );
                                },
                            },
                            {
                                title: "状态",
                                dataIndex: "status",
                                key: "status",
                                width: 80,
                                render: (status) => getStatusTag(status),
                            },
                            {
                                title: "在列表中",
                                key: "inList",
                                width: 80,
                                align: "center",
                                render: (_, record) =>
                                    record.isInCurrentList ? (
                                        <Tag color="green" icon={<CheckOutlined />}>
                                            已有
                                        </Tag>
                                    ) : record.isRemoved ? (
                                        <Tag color="orange">已移除</Tag>
                                    ) : null,
                            },
                            {
                                title: "操作",
                                key: "action",
                                width: 80,
                                align: "center",
                                render: (_, record) =>
                                    record.isInCurrentList ? (
                                        <Button type="text" disabled size="small">
                                            已添加
                                        </Button>
                                    ) : (
                                        <Button type="primary" size="small" icon={<PlusCircleOutlined />} onClick={() => addOperationFlight(record)}>
                                            添加
                                        </Button>
                                    ),
                            },
                        ]}
                    />

                    {addFlightSearchResults.length > 0 && (
                        <div style={{ marginTop: 8, color: "#666", fontSize: 12 }}>
                            共找到 {addFlightSearchResults.length} 个航班， 其中 {addFlightSearchResults.filter((f) => f.isInCurrentList).length} 个已在对比列表中
                        </div>
                    )}
                </Modal>
            </Card>
        </div>
    );
});

export default FlightCompare;
